import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VerticalAlignTopRoundedIcon from '@mui/icons-material/VerticalAlignTopRounded';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, GlobalStyles,
    IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import React from 'react';
import type { IVerbaScholaAiPreview, IVerbaScholaAiRejectedEdit, IVerbaScholaAiReviewChange, IVerbaScholaAiReviewEdit } from '../api/VerbaScholaLessonAiApi';
import { BLUE, BORDER, ERROR_TEXT, INTER, NAVY, SLATE, SOFT_BLUE, SUCCESS_TEXT, WARNING_TEXT } from '../style/verbaScholaPalette';
import { aiButtonSx } from '../style/verbaScholaStyles';
import { ACTIVITY_EDIT_KINDS, createdRows, editsTouchedBy, editWritable, expectedBeforeEdit, planReviewSave, REMOVAL_STOP_KINDS, REVIEW_PAGE_ORDER, ReviewEdit, ReviewEditKind, reviewEditKey,
    ReviewEditPlace, ReviewNames, ReviewPage, ReviewRowRef, ReviewVersion, sortStops, StopComparison, stopComparison, stopLabel, stopPages, stopTouches, touchesEdit, UnsaveableReason } from './proposalReviewModel';
import { PendingReviewEdit, saveReviewEdits, serverEdits } from './proposalReviewEdits';
import { LessonReviewUnkeptContext } from './lessonReviewReadOnly';
import { ProposalTextDiff } from './VerbaScholaProposalReview';
import { EMPTY_FEEDBACK_DRAFT, FeedbackReasonField, MessageFeedback, RateResponse, ReviewChat } from './VerbaScholaAiFeedback';
import { VerbaScholaApi } from '../api/VerbaScholaApi';
import type { IVerbaScholaLessonPlanSection } from '../api/VerbaScholaApi';

/**
 * Why a save failed; ``stale`` when the lesson changed since the proposal was made, so its AI changes cannot be saved;
 * ``uncertain`` when no answer came back (a lost response, a timeout, a server error), so the save may still have happened;
 * ``savedElsewhere`` when the proposal was already saved (another tab, another teacher) with another choice of changes;
 * ``rejected``: the teacher's edits that refused the whole save (their text changed meanwhile, or they are not valid).
 */
export interface ReviewSaveFailure { message: string; stale: boolean; uncertain?: boolean; savedElsewhere?: boolean; rejected?: IVerbaScholaAiRejectedEdit[] }

/** What the copilot or generation panel that loaded a proposal lends the page review. */
export interface ProposalReviewAttachment {
    preview: IVerbaScholaAiPreview;
    /**
     * Saves Your version in one transaction: the chosen review stops (with the rest of their groups; none: ``[]``) and the
     * teacher's ``manualEdits``, and closes the proposal, as the attempt ``attemptToken``, which settles it if its answer
     * is lost. Resolves null once saved, or why it was not (nothing is saved then).
     */
    apply: (keys: string[], attemptToken: string, manualEdits: IVerbaScholaAiReviewEdit[]) => Promise<ReviewSaveFailure | null>;
    close: () => void;
    /** Closes the proposal without saving any of its changes, when the teacher saves only their own edits. */
    dismiss?: (reason?: string) => Promise<string | null>;
    /** Why saving is unavailable right now, for example an unsent copilot message. */
    applyBlockReason?: string | null;
    /**
     * A proposal whose save deletes saved cards or exercises (``replace``) or replaces saved text, manual edits included
     * (``regenerate``): the Save dialog asks the teacher to confirm what the chosen changes delete or replace.
     */
    confirmReplacement?: 'replace' | 'regenerate';
    /** Pictures the teacher chose in place of proposed ones, by proposed URL. */
    pictureOverrides?: Record<string, string>;
    /** The proposal this one refines: its review's choices and edits carry over. */
    continuesFrom?: number;
    /** What the copilot history offers this review, when the proposal is in it. */
    chat?: ReviewChat;
    /**
     * A saved version shown again, read-only: the changes its save kept (``savedKeys``) and the teacher's edits as
     * saved; Before is the lesson the proposal was made from (the preview's ``before_content``).
     */
    replay?: { savedKeys: string[]; edits: ReviewEdit[] };
}

/** The edits one save writes, and those it cannot (to items it does not create, or inside ones it removes). */
export interface ReviewSavePlan { send: ReviewEdit[]; unsaveable: { edit: ReviewEdit; reason: UnsaveableReason }[] }

export interface ProposalReviewSession {
    page: ReviewPage;
    /** A saved version shown again: nothing can be chosen, edited or saved. */
    replay: boolean;
    preview: IVerbaScholaAiPreview;
    attachment: ProposalReviewAttachment;
    /** Stops shown on this page, in page order. */
    stops: IVerbaScholaAiReviewChange[];
    /** Stops another builder page shows. */
    otherStops: IVerbaScholaAiReviewChange[];
    /** The builder page that shows a stop. */
    pageOf: (stop: IVerbaScholaAiReviewChange) => ReviewPage;
    index: number;
    /** The current stop's place among every stop, counting Description, Lesson plan, then Exercises. */
    position: number;
    total: number;
    current: IVerbaScholaAiReviewChange | null;
    /** Other stops that including (or leaving out) the current one would include (or leave out) with it, on any page. */
    linked: IVerbaScholaAiReviewChange[];
    version: ReviewVersion;
    /** Every stop in the teacher's version, in proposal order. */
    selectedStops: IVerbaScholaAiReviewChange[];
    /** Stops the teacher has had on screen as the current change, on any page; Include all does not count. */
    reviewedCount: number;
    /** Stops in the teacher's version that have never been the current change. */
    unreviewedSelected: IVerbaScholaAiReviewChange[];
    /** The teacher's own edits to Your version, kept with the choices until the review is saved. */
    edits: ReviewEdit[];
    /** Changes whenever the edits change. */
    editsKey: string;
    /** Your version is on screen and its content may be edited. */
    editable: boolean;
    /**
     * Keeps an edit to one row of Your version in the review draft; ``before``: the saved values of those fields;
     * ``place``: for an activity row, the exercise and activity it sits in.
     */
    editRow: (kind: ReviewEditKind, ref: ReviewRowRef, fields: Record<string, unknown>, before?: Record<string, unknown>, place?: ReviewEditPlace) => void;
    /**
     * Keeps an added, removed or reordered answer, sentence or line in the review draft. Removing a row the teacher added
     * drops its addition (and its place in an order); removing a saved row drops the edits to it.
     */
    changeRows: (edit: ReviewEdit) => void;
    /** The lesson changed since the proposal was made: its AI changes cannot be saved, the teacher's own edits still can. */
    outdated: boolean;
    /**
     * Why outdated, when not the lesson: another tab, window or teacher closed the proposal, so no save of this review
     * (not even of the edits alone) is taken any more.
     */
    savedElsewhere: boolean;
    /** How it was closed there: saved from another choice of changes, or dismissed. */
    closedElsewhere: 'applied' | 'dismissed' | null;
    /** Saves only the teacher's edits to saved rows, on the current lesson, and closes the review. */
    saveEditsOnly: () => void;
    /** Every stop in review order: Description, Lesson plan, then Exercises. */
    orderedStops: IVerbaScholaAiReviewChange[];
    reviewed: (stop: IVerbaScholaAiReviewChange) => boolean;
    /** Whether the teacher edited a row this stop changes. */
    edited: (stop: IVerbaScholaAiReviewChange) => boolean;
    /** The next stop never shown as the current change, after the current one, wrapping around. */
    nextUnreviewed: IVerbaScholaAiReviewChange | null;
    /** Shows a stop, opening its builder page when another page shows it. */
    goToStop: (stop: IVerbaScholaAiReviewChange) => void;
    /** Whether the current change's rows carry the teacher's edits. */
    currentEdited: boolean;
    /** A selection change waiting for the teacher, because it would replace or drop their edits. */
    pendingChoice: PendingReviewChoice | null;
    /** True when this review started from the choices and edits of the proposal it refines. */
    carriedOver: boolean;
    /** A step that would redraw the page waits: an open editor holds text not kept in Your version. */
    pendingLeave: boolean;
    /** Why saving this review waits: edits from an earlier save still to be saved or discarded, or a save still under way. */
    earlierUnsaved: string | null;
    /** A save of this review is waiting for its outcome: the choices and edits are held as they are until it is known. */
    held: boolean;
    /**
     * The other half of a rewrite: for a saved card or exercise the proposal removes, the new one of its title that
     * replaces it, and the other way round; null for any other change. Where a change's own item is not on screen (a
     * removed one in AI proposal, a new one in Before), the page shows and highlights this one instead.
     */
    rewritePair: (stop: IVerbaScholaAiReviewChange) => IVerbaScholaAiReviewChange | null;
    /** The changes counted as selected: listed changes the selection keeps any of (a rewrite once, either half included). */
    chosenStops: IVerbaScholaAiReviewChange[];
    /** Edits that refused the last save (all or nothing), with why; the teacher redoes or leaves them out. */
    rejectedEdits: { edit: ReviewEdit; reason: string; current?: Record<string, unknown>; message?: string }[];
    /** Leaves the refused edits out of Your version, so the rest can be saved. */
    dropRejected: () => void;
    /**
     * Keeps the teacher's edits that were refused because the lesson changed meanwhile: the values the lesson holds now
     * (shown with them) become what the next save expects, so it replaces them, while any later change still stops it.
     */
    keepRejected: () => void;
    /** Leaves these edits out of Your version. */
    dropEdits: (edits: ReviewEdit[]) => void;
    /** This proposal was just saved from this review, which is closing: its pages need no new projection of it. */
    saved: boolean;
    /** What Save your version writes with the included changes, and the edits it cannot write. */
    savePlan: ReviewSavePlan;
    /** What Save my edits only writes on the current lesson, and the edits to items only this proposal adds. */
    editsOnlyPlan: ReviewSavePlan;
    /** The activity edits Your version's projection writes: exactly those its save would send, with their expected values. */
    projectionEdits: PendingReviewEdit[];
    /** Edits carried over from the proposal this one refines whose items it no longer has: listed, never saved. */
    setAside: ReviewEdit[];
    dismissSetAside: () => void;
    /** Runs a step that redraws the page (another builder page, for example), asking first about an open editor's unkept text. */
    guard: (run: () => void) => void;
    resolveLeave: (discard: boolean) => void;
    resolveChoice: (answer: 'keep' | 'ai' | 'cancel') => void;
    /** Changes whenever the teacher's version changes. */
    selectionKey: string;
    included: (stop: IVerbaScholaAiReviewChange) => boolean;
    /** Whether the version on screen has this stop's change. */
    shows: (stop: IVerbaScholaAiReviewChange) => boolean;
    /** Changes whenever the teacher moves to a stop, switches version or changes the selection, to restart its highlight. */
    focusKey: number;
    saving: boolean;
    saveError: string | null;
    setVersion: (version: ReviewVersion) => void;
    setIncluded: (stop: IVerbaScholaAiReviewChange, include: boolean) => void;
    /** Includes or leaves out several changes as one choice (a family in the list, a rewrite's version); asks as a click would. */
    /** Shows the next change on this page after a choice answered the current one, past a declined new exercise's activities. */
    moveOn: (declined?: string) => void;
    /** Includes or leaves out several changes at once; ``advance`` then moves on to the next change, once the choice is made. */
    setManyIncluded: (changes: { stop: IVerbaScholaAiReviewChange; include: boolean }[], advance?: boolean) => void;
    includeAll: () => void;
    clearSelection: () => void;
    /** Moves one stop on, or back; past this page's first or last stop it opens the next page with stops. */
    go: (delta: number) => void;
    goTo: (key: string) => void;
    save: (dismissReason?: string) => void;
    close: () => void;
    /** Opens the copilot at the proposal's findings (they are listed there only), or null where the page cannot. */
    showFindings: (() => void) | null;
    /** Opens the copilot with the cursor in its message box. */
    showComposer: (() => void) | null;
    chat?: ReviewChat;
    /** Rates this proposal, on its chat message when it has one, whatever history the page has loaded. */
    rate: RateResponse;
    label: (stop: IVerbaScholaAiReviewChange) => string;
}

/** A selection change that would replace edited fields with AI values, or drop edits to a row it adds or removes. */
export interface PendingReviewChoice {
    next: Set<string>;
    replaced: { edit: ReviewEdit; fields: string[] }[];
    dropped: ReviewEdit[];
    /** What follows once the choice is made (moving on to the next change), never when it is cancelled. */
    then?: () => void;
}

interface ReviewRegistry {
    attach: (attachment: ProposalReviewAttachment) => void;
    detach: (generationId: number) => void;
    session: ProposalReviewSession | null;
    /** Whether the teacher kept choices or edits in a proposal's review (stored in this browser). */
    hasDraft: (generationId: number) => boolean;
}

const ReviewContext = React.createContext<ReviewRegistry | null>(null);

/** The page review of the proposal open in this page's copilot or generation panel, if any. */
export function useProposalReviewSession(): ProposalReviewSession | null {
    return React.useContext(ReviewContext)?.session ?? null;
}

/** Whether this page shows proposals on the page (a builder page with a page review). */
export function useProposalReviewAvailable(): boolean {
    return Boolean(React.useContext(ReviewContext));
}

/**
 * Shows a loaded pending proposal on the page while the caller keeps it open. Once the page reviews another proposal
 * instead (opened from elsewhere in the copilot), ``onSuperseded`` closes the caller's card, so no second card offers
 * its own Apply beside the review.
 */
export function useProposalReviewAttachment(attachment: ProposalReviewAttachment | null, onSuperseded?: () => void) {
    const registry = React.useContext(ReviewContext);
    const attach = registry?.attach;
    const detach = registry?.detach;
    const generationId = attachment?.preview.generation.id;
    React.useEffect(() => {
        if (!attach || !attachment) return;
        attach(attachment);
    }, [attach, attachment]);
    React.useEffect(() => {
        if (!detach || generationId === undefined) return;
        return () => detach(generationId);
    }, [detach, generationId]);
    // Which of the caller's proposals the page showed: one the caller opened since (its own next proposal, not yet
    // shown) is never mistaken for another panel's taking over.
    const shown = registry?.session?.preview.generation.id;
    const shownHere = React.useRef<number | null>(null);
    const superseded = React.useRef(onSuperseded);
    superseded.current = onSuperseded;
    React.useEffect(() => {
        if (generationId === undefined) { shownHere.current = null; return; }
        if (shown === generationId) shownHere.current = generationId;
        else if (shown !== undefined && shownHere.current === generationId) { shownHere.current = null; superseded.current?.(); }
    }, [shown, generationId]);
}

export const REVIEW_HIGHLIGHT_STYLES = <GlobalStyles styles={{
    '@keyframes reviewChangeFlash': {
        '0%, 100%': { boxShadow: 'inset 0 0 0 0 rgba(47, 111, 237, 0)' },
        '30%, 70%': { boxShadow: 'inset 0 0 0 8px rgba(47, 111, 237, 0.22)' },
    },
    // Above component styles that clear an outline, and drawn inside the element so a clipping card keeps it whole.
    'html body [data-review-current="true"]': { outline: `3px solid ${BLUE}`, outlineOffset: -3 },
    'html body [data-review-current="true"][data-review-flash="true"]': { animation: 'reviewChangeFlash 900ms ease-out 2' },
    '@media (prefers-reduced-motion: reduce)': { 'html body [data-review-current="true"][data-review-flash="true"]': { animation: 'none' } },
}} />;

/** Where a review opened from another page starts: its first or last stop, or one stop by key, in a version. */
export interface ReviewPageStart {
    change?: string | null;
    version?: ReviewVersion | null;
    /** Arriving to refine: the review leaves a narrow window's copilot open. */
    keepCopilot?: boolean;
}

export type OpenReviewPage = (page: ReviewPage, generationId: number, start?: ReviewPageStart) => void;

/** The search part of a builder page link that reviews a proposal. */
export function reviewPageSearch(generationId: number, start: ReviewPageStart = {}): string {
    const params = new URLSearchParams({ proposal: String(generationId) });
    if (start.change) params.set('change', start.change);
    if (start.version) params.set('version', start.version);
    if (start.keepCopilot) params.set('copilot', 'open');
    return `?${params.toString()}`;
}

/** The start a builder page link asks for, read from its search part. */
export function reviewPageStart(search: string): ReviewPageStart & { generationId?: number } {
    const params = new URLSearchParams(search);
    const version = params.get('version');
    return { generationId: Number(params.get('proposal')) || undefined, change: params.get('change'),
        version: version === 'before' || version === 'proposal' || version === 'yours' ? version : null, keepCopilot: params.get('copilot') === 'open' };
}

// Review drafts and unsaved edits are kept in this browser (``localStorage``), per teacher, so they survive moving
// between builder pages, reloading and closing the tab; they are never part of the lesson until saved. Each entry
// records when it was written; entries not written for REVIEW_STORE_DAYS are removed.
const REVIEW_STORE_DAYS = 30;
const reviewStorePrefixes = ['vs-proposal-review-choices:', 'vs-proposal-review-unsaved:'];
/** False after this browser refused to store a draft: leaving the page would then lose it, so the page asks first. */
let reviewStoreWorks = true;
let reviewStorePruned = false;

function storeRead(key: string): Record<string, unknown> | null {
    try {
        const stored: unknown = JSON.parse(window.localStorage.getItem(key) ?? 'null');
        return stored && typeof stored === 'object' ? stored as Record<string, unknown> : null;
    } catch { return null; }
}

/** Stores (or, for null, removes) one entry; false when the browser refused it. */
function storeWrite(key: string, value: object | null): boolean {
    try {
        if (value) window.localStorage.setItem(key, JSON.stringify({ ...value, savedAt: Date.now() }));
        else window.localStorage.removeItem(key);
        if (value) reviewStoreWorks = true;
        return true;
    } catch {
        if (value) reviewStoreWorks = false;
        return false;
    }
}

/** Removes review entries not written for REVIEW_STORE_DAYS: proposals saved, dismissed or left elsewhere. Runs once per load. */
function pruneReviewStore() {
    if (reviewStorePruned) return;
    reviewStorePruned = true;
    try {
        const oldest = Date.now() - REVIEW_STORE_DAYS * 24 * 60 * 60 * 1000;
        const keys = Array.from({ length: window.localStorage.length }, (_value, index) => window.localStorage.key(index))
            .filter((key): key is string => key !== null && reviewStorePrefixes.some(prefix => key.startsWith(prefix)));
        for (const key of keys) {
            const savedAt = storeRead(key)?.savedAt;
            if (typeof savedAt !== 'number' || savedAt < oldest) window.localStorage.removeItem(key);
        }
    } catch { /* Storage is unavailable: nothing to remove. */ }
}

// The teacher's choices for each open proposal (by apply group), the stops already reviewed and the kept edits. The
// teacher is ``owner`` (their user id); without one, the draft lasts only while the page is open.
interface ReviewChoices { groups: Set<string>; seen: Set<string>; edits: ReviewEdit[]; setAside: ReviewEdit[] }
const choicesByProposal = new Map<string, { groups: string[]; seen: string[]; edits: ReviewEdit[]; setAside: ReviewEdit[] }>();
const editKinds = new Set<string>(['lesson', 'plan_section', 'generated_material', 'vocabulary_target', 'grammar_target', 'exercise', ...ACTIVITY_EDIT_KINDS]);
const rowActions = new Set(['create', 'delete', 'order']);

function storedEdits(value: unknown): ReviewEdit[] {
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is ReviewEdit => {
        if (!item || typeof item !== 'object') return false;
        const edit = item as ReviewEdit;
        if (!editKinds.has(edit.kind) || !edit.fields || typeof edit.fields !== 'object') return false;
        if (edit.action === undefined) return true;
        // An added, removed or reordered activity row keeps its shape. An order of a saved list stored before orders
        // recorded the saved order they were made from cannot be saved, so it is not restored.
        if (!rowActions.has(edit.action) || !ACTIVITY_EDIT_KINDS.has(edit.kind)) return false;
        if (edit.action === 'create') return Boolean(edit.clientKey && edit.parent?.kind);
        if (edit.action === 'order') return Boolean(edit.parent?.kind) && Array.isArray(edit.order) && (Boolean(edit.parent?.clientKey) || Array.isArray(edit.before?.order));
        if (edit.clientKey) return true;
        // A removal of a saved row is saved only with the values it showed; one stored before removals kept them would be refused.
        return edit.id !== undefined && Boolean(edit.before) && typeof edit.before === 'object' && Object.keys(edit.before!).length > 0;
    });
}
const choicesStorageKey = (owner: number, generationId: number) => `vs-proposal-review-choices:${owner}:${generationId}`;

function readChoices(owner: number | undefined, generationId: number): ReviewChoices {
    const kept = choicesByProposal.get(`${owner}:${generationId}`);
    if (kept) return { groups: new Set(kept.groups), seen: new Set(kept.seen), edits: kept.edits, setAside: kept.setAside };
    const list = (value: unknown) => Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
    // Storage can be unavailable; the review then starts with nothing selected.
    const record = owner === undefined ? null : storeRead(choicesStorageKey(owner, generationId));
    if (record) return { groups: new Set(list(record.groups)), seen: new Set(list(record.seen)), edits: storedEdits(record.edits), setAside: storedEdits(record.setAside) };
    return { groups: new Set(), seen: new Set(), edits: [], setAside: [] };
}

function writeChoices(owner: number | undefined, generationId: number, choices: ReviewChoices) {
    const value = { groups: [...choices.groups], seen: [...choices.seen], edits: choices.edits, setAside: choices.setAside };
    // The in-memory copy covers moving between pages even when the browser cannot store it.
    choicesByProposal.set(`${owner}:${generationId}`, value);
    if (owner !== undefined) storeWrite(choicesStorageKey(owner, generationId),
        value.groups.length || value.seen.length || value.edits.length || value.setAside.length ? value : null);
}

function forgetChoices(owner: number | undefined, generationId: number) {
    choicesByProposal.delete(`${owner}:${generationId}`);
    if (owner !== undefined) storeWrite(choicesStorageKey(owner, generationId), null);
}

/** A selection with everything its groups require, and every activity of a new exercise chosen without any. */
function withRequirements(stops: IVerbaScholaAiReviewChange[], groups: Set<string>): Set<string> {
    const requires = new Map<string, string[]>();
    const exerciseGroups = new Map(stops.filter(stop => stop.kind === 'exercise_addition' && stop.client_key).map(stop => [stop.client_key!, stop.group]));
    const activities = new Map<string, Set<string>>();
    for (const stop of stops) {
        requires.set(stop.group, [...(requires.get(stop.group) ?? []), ...(stop.requires ?? [])]);
        const exercise = stop.kind === 'exercise_addition_step' && stop.parent_key ? exerciseGroups.get(stop.parent_key) : undefined;
        if (exercise) activities.set(exercise, new Set([...(activities.get(exercise) ?? []), stop.group]));
    }
    const next = new Set<string>();
    const pending = [...groups];
    while (pending.length) {
        const group = pending.pop()!;
        if (next.has(group)) continue;
        next.add(group);
        pending.push(...(requires.get(group) ?? []));
    }
    for (const [exercise, steps] of activities) if (next.has(exercise) && ![...steps].some(step => next.has(step))) steps.forEach(step => next.add(step));
    return next;
}

const sameRowRef = (a: ReviewRowRef, b: ReviewRowRef) => a.clientKey ? b.clientKey === a.clientKey : !b.clientKey && b.id === a.id;

/**
 * A list's new order, keeping the rows its earlier order named that the new one does not: each goes right after the
 * nearest row before it there that the new order names (or first, when none does).
 */
function mergedOrder(earlier: ReviewRowRef[], latest: ReviewRowRef[], saved?: unknown[]): ReviewRowRef[] {
    const leading: ReviewRowRef[] = [];
    const following = new Map<ReviewRowRef, ReviewRowRef[]>();
    let anchor: ReviewRowRef | undefined;
    for (const ref of earlier) {
        // A saved row the list no longer holds (removed elsewhere since) is not carried over.
        if (saved && !ref.clientKey && !saved.includes(ref.id) && !latest.some(item => sameRowRef(item, ref))) continue;
        const named = latest.find(item => sameRowRef(item, ref));
        if (named) anchor = named;
        else if (anchor) following.set(anchor, [...(following.get(anchor) ?? []), ref]);
        else leading.push(ref);
    }
    if (!leading.length && !following.size) return latest;
    return [...leading, ...latest.flatMap(ref => [ref, ...(following.get(ref) ?? [])])];
}

/**
 * The edits without those whose draft entries are ``leaving``. A row the teacher added leaves every order of its list
 * with its addition, as when it is removed on the page; an order left with fewer than two rows orders nothing.
 */
function withoutEdits(edits: ReviewEdit[], leaving: Set<string>): ReviewEdit[] {
    const added = new Set(edits.filter(edit => edit.action === 'create' && edit.clientKey && leaving.has(reviewEditKey(edit.kind, edit)))
        .map(edit => `${edit.kind}:${edit.clientKey}`));
    const kept = edits.filter(edit => !leaving.has(reviewEditKey(edit.kind, edit)));
    if (!added.size) return kept;
    const gone = (edit: ReviewEdit, ref: ReviewRowRef) => Boolean(ref.clientKey) && added.has(`${edit.kind}:${ref.clientKey}`);
    return kept.map(edit => edit.action === 'order' && (edit.order ?? []).some(ref => gone(edit, ref))
        ? { ...edit, order: (edit.order ?? []).filter(ref => !gone(edit, ref)) } : edit)
        .filter(edit => edit.action !== 'order' || (edit.order ?? []).length >= 2);
}

const EARLIER_EDITS_WAITING ='Edits from an earlier save are still waiting (see the notice above the page). Save or discard them first; this review stays as it is. '
    + 'Saving them changes the lesson, which makes this proposal out of date (your own edits here can still be saved); discarding them keeps it.';
const SAVE_UNDER_WAY = 'Another save of this lesson\u2019s review is still finishing in another tab, or an earlier save\u2019s outcome is still being checked. Save waits for it; this review stays as it is.';
const HELD_BY_SAVE = 'Your save of this review is still being checked, so the review is held as it is (no changes to the choices or edits) until the server answers; it is asked every quarter minute. '
    + 'If that save went through, the review closes and your edits are offered above the page.';
const SAVED_ELSEWHERE_NO_EDITS = 'This proposal was already saved or closed in another tab or window, so the changes chosen here were not saved.';
const SAVED_ELSEWHERE = 'This proposal was already saved or closed in another tab or window, so the changes chosen here were not saved. '
    + 'Try again saves each edit only where its text still matches what you saw.';

/** A short key for a list of stop keys, for React keys and effect dependencies. */
function keyOf(keys: string[]): string {
    let hash = 5381;
    for (const key of keys) for (let index = 0; index < key.length; index++) hash = ((hash * 33) ^ key.charCodeAt(index)) >>> 0;
    return `${keys.length}:${hash.toString(36)}`;
}

/** Edits that stayed unsaved after the review's AI changes were saved, offered again outside the review. */
export interface UnsavedReviewEdits {
    /** Field edits Try again writes, each only where its fields still hold what the review showed. */
    edits: PendingReviewEdit[]; error: string; retry: () => void; discard: () => void; retrying: boolean;
    /** Edits skipped because the lesson changed those fields since the review showed them, or their item is not there; shown so the teacher can redo them. */
    conflicts: PendingReviewEdit[];
    /**
     * Added, removed and moved answers and lines: they went only with the Apply that was not made, and Try again never
     * writes them, so they are listed apart to redo on the page.
     */
    structural: PendingReviewEdit[];
    /** Some of them could not be stored in this browser: they last only while the page stays open. */
    unstored: boolean;
}

// Edits still to be written after a review's AI changes were saved. Each save keeps its own record (``token``), so a
// save in one tab never replaces or removes another's. A record is stored before the AI save starts (``awaiting``: that
// save's outcome is not known yet), marked ``writing`` while a tab writes its edits, and kept until each edit is written
// or the teacher discards them. ``proposalId`` is the reviewed proposal; ``generationId`` finds new rows' ids, and is
// null when no AI change was kept. ``updatedAt`` is when it was last stored; ``tab`` is the tab that started the save.
interface UnsavedRecord {
    token: string; tab: string; startedAt: number; updatedAt: number;
    proposalId: number | null; generationId: number | null; edits: PendingReviewEdit[]; error: string; conflicts: PendingReviewEdit[];
    awaiting?: 'applied' | 'dismissed'; writing?: boolean;
    /** Its edits went with the Apply (one transaction): saved with it, nothing is left to write. */
    atomic?: boolean;
    /** Only in this page's memory: the browser refused to store it. */
    unstored?: boolean;
}
/**
 * How long another tab's save in progress (waiting for its AI save, or writing its edits) is left to that tab. Settling
 * an attempt is final, so this proves nothing; it only keeps two tabs from settling or writing the same save at once.
 */
const UNSAVED_IN_FLIGHT_MS = 3 * 60 * 1000;
const unsavedPrefix = (owner: number | undefined, lessonId: number) => `vs-proposal-review-unsaved:${owner}:${lessonId}:`;
/** Records the browser refused to store (full or blocked storage), kept so this page still offers them; leaving asks first. */
const unstoredRecords = new Map<string, UnsavedRecord>();
/** Stored records the browser refused to remove: their stored copy is out of date and is not offered again. */
const unremovedKeys = new Set<string>();
const recent = (record: UnsavedRecord) => Date.now() - record.updatedAt < UNSAVED_IN_FLIGHT_MS;

/** This tab, kept across its reloads (``sessionStorage``): a save it started is settled at once, never taken for another tab's. */
const thisTab = (() => {
    try {
        const stored = window.sessionStorage.getItem('vs-proposal-review-tab');
        if (stored) return stored;
        const created = crypto.randomUUID();
        window.sessionStorage.setItem('vs-proposal-review-tab', created);
        return created;
    } catch { return crypto.randomUUID(); }
})();

function newUnsavedRecord(fields: Pick<UnsavedRecord, 'proposalId' | 'generationId' | 'edits' | 'awaiting' | 'atomic'>): UnsavedRecord {
    const now = Date.now();
    return { token: crypto.randomUUID(), tab: thisTab, startedAt: now, updatedAt: now, error: 'Saving was interrupted.', conflicts: [], ...fields };
}

/** A save another tab is still making: waiting for its AI save, or writing its edits, and stored recently. */
function underWayInAnotherTab(record: UnsavedRecord): boolean {
    return record.tab !== thisTab && Boolean(record.awaiting || record.writing) && recent(record);
}

/** Every record of a teacher's unsaved edits to a lesson, stored from any tab or kept here unstored, oldest first. */
function readUnsavedRecords(owner: number | undefined, lessonId: number): UnsavedRecord[] {
    const prefix = unsavedPrefix(owner, lessonId);
    const byToken = new Map((owner === undefined ? [] : storedUnsavedRecords(prefix))
        .filter(record => !unremovedKeys.has(prefix + record.token)).map(record => [record.token, record]));
    // A copy kept here because storing an update failed is newer than the stored one, which it replaces.
    for (const [key, record] of unstoredRecords) {
        const stored = key.startsWith(prefix) ? byToken.get(record.token) : undefined;
        if (key.startsWith(prefix) && (!stored || record.updatedAt >= stored.updatedAt)) byToken.set(record.token, record);
    }
    return [...byToken.values()].sort((a, b) => a.startedAt - b.startedAt);
}

function storedUnsavedRecords(prefix: string): UnsavedRecord[] {
    try {
        const keys = Array.from({ length: window.localStorage.length }, (_value, index) => window.localStorage.key(index))
            .filter((key): key is string => key !== null && key.startsWith(prefix));
        return keys.flatMap((key): UnsavedRecord[] => {
            const record = storeRead(key);
            const edits = storedEdits(record?.edits) as PendingReviewEdit[];
            const conflicts = storedEdits(record?.conflicts) as PendingReviewEdit[];
            const awaiting = record?.awaiting === 'applied' || record?.awaiting === 'dismissed';
            if (!record || (!edits.length && !conflicts.length && !awaiting)) return [];
            const updatedAt = typeof record.savedAt === 'number' ? record.savedAt : 0;
            return [{
                token: key.slice(prefix.length), tab: typeof record.tab === 'string' ? record.tab : '',
                startedAt: typeof record.startedAt === 'number' ? record.startedAt : updatedAt, updatedAt,
                proposalId: typeof record.proposalId === 'number' ? record.proposalId : null,
                generationId: typeof record.generationId === 'number' ? record.generationId : null, edits, conflicts,
                error: typeof record.error === 'string' ? record.error : 'Saving was interrupted.',
                ...(record.awaiting === 'applied' || record.awaiting === 'dismissed' ? { awaiting: record.awaiting } : {}),
                ...(record.writing === true ? { writing: true } : {}),
                ...(record.atomic === true ? { atomic: true } : {}),
            }];
        });
    } catch { return []; }
}

function writeUnsaved(owner: number | undefined, lessonId: number, record: UnsavedRecord) {
    const key = unsavedPrefix(owner, lessonId) + record.token;
    // A save waiting to be settled is kept without edits too (an AI-only save), so its outcome is still settled.
    const value = record.edits.length || record.conflicts.length || record.awaiting ? record : null;
    // A record the browser will not store is kept here instead, so the page still offers its edits while it stays open.
    if (value && !(owner !== undefined && storeWrite(key, value))) unstoredRecords.set(key, { ...record, updatedAt: Date.now(), unstored: true });
    else if (value) { unstoredRecords.delete(key); unremovedKeys.delete(key); }
    else removeUnsaved(owner, lessonId, record.token);
}

function removeUnsaved(owner: number | undefined, lessonId: number, token: string) {
    const key = unsavedPrefix(owner, lessonId) + token;
    unstoredRecords.delete(key);
    if (owner !== undefined && !storeWrite(key, null)) unremovedKeys.add(key);
}

/**
 * How an Apply attempt ended, settled by the server for good (it waits for an Apply still running, and refuses the attempt
 * if it arrives later): ``applied`` when this attempt saved the proposal; ``savedElsewhere`` when it did not and another
 * save closed the proposal; ``dismissedElsewhere`` when it did not and a dismissal closed it; ``open`` when it did not and
 * the proposal is still open, so its review still holds the edits; ``unknown`` when the server could not be asked.
 */
type AttemptOutcome = 'applied' | 'savedElsewhere' | 'dismissedElsewhere' | 'open' | 'unknown';
/** How another tab, window or teacher closed the proposal, when this attempt did not: saved from another choice, or dismissed. */
type ClosedElsewhere = 'applied' | 'dismissed';
function closedElsewhereAs(outcome: AttemptOutcome | null): ClosedElsewhere | null {
    return outcome === 'savedElsewhere' ? 'applied' : outcome === 'dismissedElsewhere' ? 'dismissed' : null;
}

async function attemptOutcome(lessonId: number, proposalId: number, token: string): Promise<AttemptOutcome> {
    try {
        const settled = (await VerbaScholaApi.settleLessonAiApplyAttempt(lessonId, proposalId, token)).data;
        if (settled.outcome === 'applied') return 'applied';
        const status = settled.generation.proposal_status;
        return status === 'applied' ? 'savedElsewhere' : status === 'dismissed' ? 'dismissedElsewhere' : 'open';
    } catch { return 'unknown'; }
}

/**
 * Settles one record whose AI save's outcome was not known (a lost answer, a reload or closed tab during the save): saved,
 * its edits are due; closed by another save, its edits are offered too, each written only where it still matches; still
 * open, the save did not happen and the edits are still in that proposal's review. Unknown leaves it waiting.
 */
async function settleRecord(owner: number | undefined, lessonId: number, record: UnsavedRecord): Promise<AttemptOutcome | null> {
    // Older records waited for a dismissal, which their edits never depended on.
    if (record.awaiting === 'dismissed') { writeUnsaved(owner, lessonId, { ...record, awaiting: undefined }); return null; }
    if (record.proposalId === null) return null;
    const outcome = await attemptOutcome(lessonId, record.proposalId, record.token);
    // Saved: edits that went with the Apply were saved with it; older records still have theirs to write.
    if (outcome === 'applied') { if (record.atomic) removeUnsaved(owner, lessonId, record.token); else writeUnsaved(owner, lessonId, { ...record, awaiting: undefined }); }
    else if (closedElsewhereAs(outcome)) writeUnsaved(owner, lessonId, { ...record, awaiting: undefined, atomic: undefined, error: SAVED_ELSEWHERE });
    else if (outcome === 'open') removeUnsaved(owner, lessonId, record.token);
    return outcome;
}

/** Why the lesson's own rules refused an edit, in words: the server names some refusals by code. */
export function refusalText(message: string): string {
    if (message === 'authoring_constraint_conflict') return 'saved student work or other saved content still uses it';
    if (message === 'lesson_audio_not_ready') return 'its new Chinese audio cannot be prepared while students can see the lesson';
    return /^[a-z_]+$/.test(message) ? 'the lesson’s rules do not allow it' : message;
}

/** One edit's fields and values, for a list the teacher can redo them from. */
export function editSummary(edit: ReviewEdit): string {
    const values = (fields: Record<string, unknown>) => Object.entries(fields).map(([field, value]) => `${field}: ${typeof value === 'string' ? value : JSON.stringify(value)}`).join('; ');
    // An added, removed or reordered answer or sentence names what it did.
    const [one, many] = edit.kind === 'exercise_step_choice' ? ['answer', 'answers'] : ['sentence', 'sentences'];
    if (edit.action === 'create') return `Added ${one}: ${values(edit.fields)}`;
    if (edit.action === 'delete') {
        const text = edit.before?.choice_text ?? edit.before?.sentence_text;
        return `Removed ${one}${typeof text === 'string' && text ? `: ${text}` : ''}`;
    }
    if (edit.action === 'order') return `Moved ${many}`;
    return values(edit.fields);
}

export interface ProposalReviewController extends ReviewRegistry {
    openPage: OpenReviewPage | null;
    unsavedEdits: UnsavedReviewEdits | null;
    /** Editors report text not yet kept in Your version, so redrawing the page asks first. */
    markUnkept: (key: string, unkept: boolean) => void;
    /** Runs a step that redraws the page, asking first when an editor other than ``key`` holds text not kept yet. */
    guardExcept: (key: string, run: () => void) => void;
    /**
     * Why leaving the page now would lose review work (an open editor's unkept text, a save under way), or null.
     * Kept choices and edits are stored, so they alone do not ask. Stable; the page's leave prompt calls it.
     */
    leaveWarning: () => string | null;
    /** Why the page's Refresh (which redraws the editors, the page staying open) would lose review work: an open
     * editor's unkept text, a save under way; or null. Stable. */
    refreshWarning: () => string | null;
    /** True while a review is open or saving, or edits are kept unstored, so the page's leave prompt should ask ``leaveWarning``. */
    guardsLeave: boolean;
}

/** A page's review state. The page reads ``session`` and wraps its copilot in ``ProposalReviewScope``. */
export function useProposalReviewController({ page, names, sections, onOpenPage, onActivate, onShowFindings, enabled = true, start, lessonId, userId, describeError, onEditsSaved }: {
    page: ReviewPage;
    /** The lesson whose rows the teacher's edits change. */
    lessonId?: number;
    /** The signed-in teacher: review drafts and unsaved edits are stored in this browser per teacher. */
    userId?: number;
    /** A teacher-facing message for a failed save. */
    describeError?: (caught: unknown) => string;
    /** Called after the teacher's edits were written, to reload the page. */
    onEditsSaved?: () => void;
    /** Where to start when the linked proposal opens (``?proposal=&change=&version=``); used once. */
    start?: ReviewPageStart & { generationId?: number };
    /** False while the page has unsaved edits: a review would replace the editor holding them. */
    enabled?: boolean;
    names?: ReviewNames;
    /** Saved plan sections, to order plan stops by their panel. */
    sections?: IVerbaScholaLessonPlanSection[];
    /** Opens another builder page reviewing the same proposal. */
    onOpenPage?: OpenReviewPage;
    /** Called when a proposal opens, for example to close a narrow-window copilot drawer that covers the page. */
    onActivate?: () => void;
    /** Shows the page's copilot, where the proposal's findings are listed (the review bar links to them). */
    onShowFindings?: () => void;
}): ProposalReviewController {
    const [attachment, setAttachment] = React.useState<ProposalReviewAttachment | null>(null);
    const [stopKey, setStopKey] = React.useState<string | null>(null);
    const [version, setVersionState] = React.useState<ReviewVersion>('proposal');
    const [selected, setSelected] = React.useState<Set<string>>(() => new Set());
    const [seen, setSeen] = React.useState<Set<string>>(() => new Set());
    const selectedRef = React.useRef(selected);
    selectedRef.current = selected;
    const seenRef = React.useRef(seen);
    seenRef.current = seen;
    const [edits, setEdits] = React.useState<ReviewEdit[]>([]);
    const editsRef = React.useRef(edits);
    editsRef.current = edits;
    const [setAside, setSetAside] = React.useState<ReviewEdit[]>([]);
    const setAsideRef = React.useRef(setAside);
    setAsideRef.current = setAside;
    const [pendingChoice, setPendingChoice] = React.useState<PendingReviewChoice | null>(null);
    const owner = React.useRef(userId);
    owner.current = userId;
    // Every stored record of unsaved edits to this lesson, from any tab; ``ownSaves`` are the ones this tab is saving now.
    const [records, setRecords] = React.useState<UnsavedRecord[]>(() => {
        pruneReviewStore();
        return lessonId === undefined ? [] : readUnsavedRecords(userId, lessonId);
    });
    const ownSaves = React.useRef(new Set<string>());
    const checking = React.useRef(new Set<string>());
    const attachmentRef = React.useRef<ProposalReviewAttachment | null>(null);
    /** A save settled later closed its proposal (saved, or closed by another save): its review can no longer be saved. */
    const lessonRef = React.useRef(lessonId);
    lessonRef.current = lessonId;
    const onSettled = React.useCallback((record: UnsavedRecord, outcome: AttemptOutcome | null) => {
        const closed = closedElsewhereAs(outcome);
        if ((outcome !== 'applied' && !closed) || record.proposalId === null) return;
        forgetChoices(owner.current, record.proposalId);
        if (attachedId.current === record.proposalId) {
            // Its edits, if any, are offered above the page; an AI-only save closed elsewhere is explained in the bar.
            const shown = attachmentRef.current?.preview;
            // Edits the review still holds (made after that save) cannot be saved with this proposal any more: they are
            // kept as unsaved edits, offered above the page with Try again, as a save that ran into it keeps its own.
            const held = outcome !== 'applied' && !record.edits.length && shown && lessonRef.current !== undefined
                ? planReviewSave(editsRef.current, [], shown).send.map(edit => ({ ...edit, expected: expectedBeforeEdit(edit, [], shown) })) : [];
            if (held.length) {
                writeUnsaved(owner.current, lessonRef.current!, { ...newUnsavedRecord({ proposalId: record.proposalId, generationId: record.proposalId, edits: held }), error: SAVED_ELSEWHERE });
                setRecords(readUnsavedRecords(owner.current, lessonRef.current!));
            }
            if (outcome === 'applied' || record.edits.length || held.length) attachmentRef.current?.close();
            else { setClosedElsewhere(closed); setSaveError(SAVED_ELSEWHERE_NO_EDITS); }
        }
        if (outcome === 'applied') editsSaved.current?.();
    }, []);
    const refreshUnsaved = React.useCallback(() => {
        const teacher = owner.current;
        if (lessonId === undefined || teacher === undefined) return;
        const stored = readUnsavedRecords(teacher, lessonId);
        setRecords(stored);
        for (const record of stored) {
            if (!record.awaiting || ownSaves.current.has(record.token) || checking.current.has(record.token)) continue;
            // Another tab still making this save is left to finish it; this tab's own, or one left behind, is settled now.
            if (underWayInAnotherTab(record)) continue;
            checking.current.add(record.token);
            void settleRecord(teacher, lessonId, record).then(outcome => onSettled(record, outcome))
                .finally(() => { checking.current.delete(record.token); setRecords(readUnsavedRecords(teacher, lessonId)); });
        }
    }, [lessonId, onSettled]);
    React.useEffect(() => { refreshUnsaved(); }, [refreshUnsaved, userId]);
    React.useEffect(() => {
        if (lessonId === undefined || userId === undefined) return;
        const prefix = unsavedPrefix(userId, lessonId);
        // Another tab stored, wrote or discarded edits to this lesson.
        const changed = (event: StorageEvent) => { if (event.key === null || event.key.startsWith(prefix)) refreshUnsaved(); };
        window.addEventListener('storage', changed);
        return () => window.removeEventListener('storage', changed);
    }, [lessonId, userId, refreshUnsaved]);
    // Records shown and offered again: not being saved by this tab now, not a save still under way in another tab, and
    // never one waiting to be settled, whichever tab made it: until the server answers, writing its edits could land on
    // top of an AI save that is still to come.
    const offered = React.useCallback((record: UnsavedRecord) => !record.awaiting && !underWayInAnotherTab(record)
        && (record.edits.length > 0 || record.conflicts.length > 0), []);
    const shownRecords = React.useMemo(() => records.filter(record => !ownSaves.current.has(record.token) && offered(record)), [records, offered]);
    // A save whose outcome is not known yet (waiting to be settled, or another tab's still being written) holds new saves back.
    const unsettled = React.useCallback((record: UnsavedRecord) => !ownSaves.current.has(record.token)
        && (Boolean(record.awaiting) || underWayInAnotherTab(record)), []);
    const underWayElsewhere = records.some(unsettled);
    // Such a save is settled again every quarter minute until the server answers; another tab's still under way, once
    // that tab had time to finish it.
    React.useEffect(() => {
        const waiting = records.filter(unsettled);
        if (!waiting.length) return;
        const due = Math.min(...waiting.map(record => underWayInAnotherTab(record) ? record.updatedAt + UNSAVED_IN_FLIGHT_MS : Date.now() + 15000)) - Date.now();
        const timer = window.setTimeout(refreshUnsaved, Math.max(due, 0) + 1000);
        return () => window.clearTimeout(timer);
    }, [records, unsettled, refreshUnsaved]);
    // Inline editors with text the teacher has not kept in Your version yet; leaving the view would discard it.
    const unkeptRef = React.useRef(new Set<string>());
    const [pendingLeave, setPendingLeave] = React.useState<{ run: () => void } | null>(null);
    const markUnkept = React.useCallback((key: string, unkept: boolean) => {
        if (unkept) unkeptRef.current.add(key); else unkeptRef.current.delete(key);
    }, []);
    /** Runs a step that redraws the page, asking first when an open editor holds text not kept yet. */
    const guarded = React.useCallback((run: () => void) => {
        if (unkeptRef.current.size) setPendingLeave({ run }); else run();
    }, []);
    const guardExcept = React.useCallback((key: string, run: () => void) => {
        if ([...unkeptRef.current].some(other => other !== key)) setPendingLeave({ run }); else run();
    }, []);
    const resolveLeave = React.useCallback((discard: boolean) => {
        const pending = pendingLeave;
        setPendingLeave(null);
        if (pending && discard) { unkeptRef.current.clear(); pending.run(); }
    }, [pendingLeave]);
    const [staleFailure, setStaleFailure] = React.useState(false);
    const [savedProposal, setSavedProposal] = React.useState<number | null>(null);
    // Another tab or teacher closed the proposal first: saved it from another choice of changes, or dismissed it.
    const [closedElsewhere, setClosedElsewhere] = React.useState<ClosedElsewhere | null>(null);
    const savedElsewhere = closedElsewhere !== null;
    const [carriedOver, setCarriedOver] = React.useState(false);
    const [retrying, setRetrying] = React.useState(false);
    const editsSaved = React.useRef(onEditsSaved);
    editsSaved.current = onEditsSaved;
    const [focusKey, setFocusKey] = React.useState(0);
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const lastIndex = React.useRef(0);
    const activate = React.useRef(onActivate);
    activate.current = onActivate;
    const showCopilot = React.useRef(onShowFindings);
    showCopilot.current = onShowFindings;
    // The copilot opens (a drawer slides in, a folded panel unfolds), then its findings are brought into view.
    const showFindings = React.useCallback((generationId: number) => {
        showCopilot.current?.();
        // This proposal's findings, not another card's still on the page.
        window.setTimeout(() => document.querySelector<HTMLElement>(`[data-proposal-findings="${generationId}"]`)?.scrollIntoView({ block: 'center' }), 350);
    }, []);
    const showComposer = React.useCallback(() => {
        showCopilot.current?.();
        window.setTimeout(() => document.getElementById('plan-copilot-message')?.focus(), 350);
    }, []);
    const canShowFindings = Boolean(onShowFindings);
    const enabledRef = React.useRef(enabled);
    enabledRef.current = enabled;
    const attachedId = React.useRef<number | null>(null);
    const startRef = React.useRef(start);
    const attach = React.useCallback((next: ProposalReviewAttachment) => {
        if (attachedId.current !== next.preview.generation.id) {
            attachedId.current = next.preview.generation.id;
            const linked = startRef.current?.generationId === next.preview.generation.id ? startRef.current : undefined;
            if (linked) startRef.current = undefined;
            // 'first' and 'last' come from moving past another page's stops; any other value names a stop.
            const change = linked?.change;
            setStopKey(change && change !== 'first' && change !== 'last' ? change : null);
            lastIndex.current = change === 'last' ? Number.MAX_SAFE_INTEGER : 0;
            // A saved version opens on what was saved; a review on the whole proposal.
            setVersionState(linked?.version ?? (next.replay ? 'yours' : 'proposal')); setFocusKey(value => value + 1);
            const all = next.preview.review_changes ?? [];
            // A saved version shows its own choices and edits, never this browser's draft, and stores nothing.
            let choices = next.replay ? { groups: new Set(all.filter(stop => next.replay!.savedKeys.includes(stop.key)).map(stop => stop.group)),
                seen: new Set(all.map(stop => stop.key)), edits: next.replay.edits, setAside: [] as ReviewEdit[] }
                : readChoices(owner.current, next.preview.generation.id);
            const fresh = !choices.groups.size && !choices.seen.size && !choices.edits.length && !choices.setAside.length;
            const earlier = !next.replay && fresh && next.continuesFrom !== undefined ? readChoices(owner.current, next.continuesFrom) : null;
            // A refined proposal keeps the teacher's work: the same changes stay chosen, the edits stay; nothing counts as reviewed.
            const carried = Boolean(earlier && (earlier.groups.size || earlier.edits.length));
            if (earlier && carried) {
                const stops = next.preview.review_changes ?? [];
                const groups = new Set(stops.map(stop => stop.group));
                // The refined proposal's own links decide what each carried choice needs, as a click would.
                const chosen = withRequirements(stops, new Set([...earlier.groups].filter(group => groups.has(group))));
                // An edit to a proposed new item carries over only while the carried choices still create that item; the
                // rest are set aside and listed, so the review never counts an edit its save would skip.
                const created = createdRows(stops.filter(stop => chosen.has(stop.group)), next.preview);
                const kept = earlier.edits.filter(edit => editWritable(edit, created));
                choices = { groups: chosen, seen: new Set(), edits: kept, setAside: [...earlier.setAside, ...earlier.edits.filter(edit => !kept.includes(edit))] };
                writeChoices(owner.current, next.preview.generation.id, choices);
            }
            setCarriedOver(carried);
            selectedRef.current = choices.groups; seenRef.current = choices.seen; editsRef.current = choices.edits; setAsideRef.current = choices.setAside;
            setSelected(choices.groups); setSeen(choices.seen); setEdits(choices.edits); setSetAside(choices.setAside);
            setSaveError(null); setPendingChoice(null); setStaleFailure(false); setClosedElsewhere(null);
            // Only a review that takes over the page closes a drawer covering it.
            if (!linked?.keepCopilot) queueMicrotask(() => { if (enabledRef.current) activate.current?.(); });
        }
        setAttachment(next);
    }, []);
    const detach = React.useCallback((generationId: number) => {
        if (attachedId.current !== generationId) return;
        attachedId.current = null;
        setAttachment(null);
    }, []);
    attachmentRef.current = attachment;
    const preview = attachment?.preview ?? null;
    // A save of this proposal whose outcome is not known yet holds its review as it is: if the server reports that save as
    // done, the edits it carried are the ones offered, so none may change, and no choice either, until it is known.
    const held = Boolean(preview) && records.some(record => record.awaiting && record.proposalId === preview!.generation.id
        && !ownSaves.current.has(record.token));
    const generationId = preview?.generation.id;
    const allStops = React.useMemo(() => preview?.review_changes ?? [], [preview]);
    // A rewrite (a saved card or exercise the proposal removes, and the new one of its title) is one change to review: the
    // new item's half goes with the saved one's, which is listed, counted and shown in every version; the rewrite's own
    // choice (use the new one, keep both, keep the saved one) includes either half.
    const pairedAway = React.useMemo(() => new Map(allStops.flatMap(stop => {
        if (!stop.pair_key || (stop.kind !== 'plan_section_addition' && stop.kind !== 'exercise_addition')) return [];
        const removal = allStops.find(other => other.pair_key === stop.pair_key && (other.kind === 'plan_section_removal' || other.kind === 'exercise_removal'));
        return removal ? [[stop.key, removal] as const] : [];
    })), [allStops]);
    const listedStops = React.useMemo(() => allStops.filter(stop => !pairedAway.has(stop.key)), [allStops, pairedAway]);
    const pageOf = React.useMemo(() => preview ? stopPages(preview, page) : () => page, [preview, page]);
    const stops = React.useMemo(() => preview ? sortStops(listedStops.filter(stop => pageOf(stop) === page), page, preview, sections) : [],
        [listedStops, page, pageOf, preview, sections]);
    const otherStops = React.useMemo(() => listedStops.filter(stop => pageOf(stop) !== page), [listedStops, page, pageOf]);
    const pageCounts = React.useMemo(() => Object.fromEntries(REVIEW_PAGE_ORDER.map(value =>
        [value, value === page ? stops.length : otherStops.filter(stop => pageOf(stop) === value).length])) as Record<ReviewPage, number>,
    [page, stops, otherStops, pageOf]);
    const wanted = stopKey ? pairedAway.get(stopKey)?.key ?? stopKey : null;
    const found = wanted ? stops.findIndex(stop => stop.key === wanted) : -1;
    const index = found >= 0 ? found : Math.min(lastIndex.current, Math.max(stops.length - 1, 0));
    lastIndex.current = index;
    const current = stops[index] ?? null;
    const before = REVIEW_PAGE_ORDER.slice(0, REVIEW_PAGE_ORDER.indexOf(page)).reduce((sum, value) => sum + pageCounts[value], 0);
    const position = stops.length ? before + index + 1 : 0;
    // Groups that must be saved together with others: what each requires, what requires it, and each new exercise's activities.
    const links = React.useMemo(() => {
        const requires = new Map<string, string[]>();
        const dependents = new Map<string, string[]>();
        const exerciseGroups = new Map(allStops.filter(stop => stop.kind === 'exercise_addition' && stop.client_key).map(stop => [stop.client_key!, stop.group]));
        const activities = new Map<string, Set<string>>();
        for (const stop of allStops) {
            for (const need of stop.requires ?? []) {
                requires.set(stop.group, [...(requires.get(stop.group) ?? []), need]);
                dependents.set(need, [...(dependents.get(need) ?? []), stop.group]);
            }
            const exercise = stop.kind === 'exercise_addition_step' && stop.parent_key ? exerciseGroups.get(stop.parent_key) : undefined;
            if (exercise) activities.set(exercise, new Set([...(activities.get(exercise) ?? []), stop.group]));
        }
        return { requires, dependents, activities };
    }, [allStops]);
    /** The selection after including or leaving out one stop, with everything linked to it. */
    const toggled = React.useCallback((from: Set<string>, stop: IVerbaScholaAiReviewChange, include: boolean): Set<string> => {
        const next = new Set(from);
        const pending = [stop.group];
        while (pending.length) {
            const group = pending.pop()!;
            if (next.has(group) === include) continue;
            if (include) next.add(group); else next.delete(group);
            pending.push(...((include ? links.requires : links.dependents).get(group) ?? []));
        }
        for (const [exercise, steps] of links.activities) {
            if (!next.has(exercise) || [...steps].some(step => next.has(step))) continue;
            // A new exercise is saved with at least one activity: including it alone brings them all; leaving out its last leaves it out.
            if (include) steps.forEach(step => next.add(step)); else next.delete(exercise);
        }
        // A rewrite's new item left out that way keeps its saved one: a saved item is never deleted with nothing in its
        // place unless the teacher removes it on its own.
        if (!include) for (const [additionKey, removal] of pairedAway) {
            const addition = allStops.find(stop => stop.key === additionKey);
            if (addition && from.has(addition.group) && !next.has(addition.group) && next.has(removal.group) && stop.key !== removal.key) next.delete(removal.group);
        }
        return next;
    }, [links, pairedAway, allStops]);
    const linked = React.useMemo(() => {
        if (!current) return [];
        // A rewrite's choice takes both its halves: what using the new one brings, or keeping the saved one leaves out.
        const pair = current.pair_key ? allStops.find(other => other !== current && other.pair_key === current.pair_key && rewriteHalves.has(other.kind)) : undefined;
        const using = pair ? !selected.has(pair.group) : !selected.has(current.group);
        const after = pair ? toggled(toggled(selected, current, using), pair, using) : toggled(selected, current, using);
        return allStops.filter(stop => stop.key !== current.key && stop.key !== pair?.key && (stop.group === current.group || after.has(stop.group) !== selected.has(stop.group)));
    }, [current, selected, allStops, toggled]);
    const selectedStops = React.useMemo(() => allStops.filter(stop => selected.has(stop.group)), [allStops, selected]);
    const selectionKey = React.useMemo(() => keyOf(selectedStops.map(stop => stop.key)), [selectedStops]);
    // The change on screen counts as reviewed once shown.
    const currentKey = enabled && attachment ? current?.key : undefined;
    // A rewrite shown is reviewed whole, both its halves.
    const currentPairKey = currentKey ? [...pairedAway].find(([, removal]) => removal.key === currentKey)?.[0] : undefined;
    React.useEffect(() => {
        if (!currentKey || generationId === undefined || (seenRef.current.has(currentKey) && (!currentPairKey || seenRef.current.has(currentPairKey)))) return;
        const next = new Set(seenRef.current).add(currentKey);
        if (currentPairKey) next.add(currentPairKey);
        seenRef.current = next; setSeen(next);
        writeChoices(owner.current, generationId, { groups: selectedRef.current, seen: next, edits: editsRef.current, setAside: setAsideRef.current });
    }, [currentKey, currentPairKey, generationId]);
    const reviewedCount = React.useMemo(() => listedStops.filter(stop => seen.has(stop.key)).length, [listedStops, seen]);
    const orderedStops = React.useMemo(() => preview ? REVIEW_PAGE_ORDER.flatMap(value => value === page ? stops
        : sortStops(listedStops.filter(stop => pageOf(stop) === value), value, preview)) : [], [preview, page, stops, listedStops, pageOf]);
    // The listed changes the selection keeps any of: a rewrite counts once, whichever of its halves is included.
    const chosenStops = React.useMemo(() => {
        const pairOf = new Map([...pairedAway].map(([key, removal]) => [removal.key, allStops.find(stop => stop.key === key)!]));
        return listedStops.filter(stop => selected.has(stop.group) || (pairOf.has(stop.key) && selected.has(pairOf.get(stop.key)!.group)));
    }, [listedStops, allStops, pairedAway, selected]);
    const nextUnreviewed = React.useMemo(() => {
        const start = current ? orderedStops.findIndex(stop => stop.key === current.key) : -1;
        for (let step = 1; step <= orderedStops.length; step++) {
            const stop = orderedStops[(start + step) % orderedStops.length];
            if (!seen.has(stop.key)) return stop;
        }
        return null;
    }, [orderedStops, current, seen]);
    const reviewed = React.useCallback((stop: IVerbaScholaAiReviewChange) => seen.has(stop.key), [seen]);
    const unreviewedSelected = React.useMemo(() => chosenStops.filter(stop => !seen.has(stop.key)), [chosenStops, seen]);
    const replaying = Boolean(attachment?.replay);
    const go = React.useCallback((delta: number) => {
        const step = delta < 0 ? -1 : 1;
        const target = index + delta;
        // A saved version is shown on one page only: opening another page would show that page's lesson, not the saved version.
        if (onOpenPage && preview && !replaying && otherStops.length && (!stops.length || target < 0 || target >= stops.length)) {
            // Past this page's first or last stop: the neighbouring page with stops, wrapping around.
            const here = REVIEW_PAGE_ORDER.indexOf(page);
            const next = [1, 2].map(offset => REVIEW_PAGE_ORDER[(here + step * offset + 3) % 3]).find(value => pageCounts[value] > 0);
            if (next) { onOpenPage(next, preview.generation.id, { change: step > 0 ? 'first' : 'last', version }); return; }
        }
        if (!stops.length) return;
        const following = stops[(target % stops.length + stops.length) % stops.length];
        setStopKey(following.key); setFocusKey(value => value + 1);
    }, [stops, index, onOpenPage, preview, replaying, otherStops, page, pageCounts, version]);
    // Opened on a page that holds none of its changes, a review moves to where they are.
    const sentOn = React.useRef<number | null>(null);
    React.useEffect(() => {
        if (!onOpenPage || !preview || replaying || stops.length || !otherStops.length || sentOn.current === preview.generation.id) return;
        sentOn.current = preview.generation.id;
        const target = REVIEW_PAGE_ORDER.find(value => pageCounts[value] > 0);
        if (target) onOpenPage(target, preview.generation.id, { change: 'first', version });
    }, [onOpenPage, preview, replaying, stops, otherStops, pageCounts, version]);
    const goTo = React.useCallback((key: string) => { setStopKey(key); setFocusKey(value => value + 1); }, []);
    /** The next change on this page after a choice answered the current one (past a declined new exercise's activities); the last stays. */
    const moveOn = React.useCallback((declined?: string) => {
        const following = stops.slice(index + 1).find(stop => !(declined && stop.kind === 'exercise_addition_step' && stop.parent_key === declined));
        if (following) goTo(following.key);
    }, [stops, index, goTo]);
    const goToStop = React.useCallback((shown: IVerbaScholaAiReviewChange) => {
        const stop = pairedAway.get(shown.key) ?? shown;
        const target = pageOf(stop);
        if (target === page) goTo(stop.key);
        else if (onOpenPage && preview && !replaying) onOpenPage(target, preview.generation.id, { change: stop.key, version });
    }, [pairedAway, pageOf, page, goTo, onOpenPage, preview, replaying, version]);
    const setVersion = React.useCallback((value: ReviewVersion) => { setVersionState(value); setFocusKey(key => key + 1); }, []);
    const choose = React.useCallback((next: Set<string>, nextEdits: ReviewEdit[] = editsRef.current) => {
        if (generationId === undefined || attachmentRef.current?.replay) return;
        writeChoices(owner.current, generationId, { groups: next, seen: seenRef.current, edits: nextEdits, setAside: setAsideRef.current });
        selectedRef.current = next; setSelected(next); editsRef.current = nextEdits; setEdits(nextEdits); setSaveError(null);
        // Your version changes on screen; the current change stays and is marked again.
        setFocusKey(value => value + 1);
    }, [generationId]);
    const editRow = React.useCallback((kind: ReviewEditKind, ref: ReviewRowRef, fields: Record<string, unknown>, before?: Record<string, unknown>, place?: ReviewEditPlace) => {
        if (generationId === undefined || !Object.keys(fields).length || attachmentRef.current?.replay) return;
        const key = reviewEditKey(kind, ref);
        const next = [...editsRef.current];
        const index = next.findIndex(edit => reviewEditKey(edit.kind, edit) === key);
        // The saved value of a field is the one first seen: later edits to it keep that starting point.
        if (index >= 0) next[index] = { ...next[index], fields: { ...next[index].fields, ...fields },
            ...(before || next[index].before ? { before: { ...before, ...next[index].before } } : {}) };
        else next.push({ kind, ...(ref.clientKey ? { clientKey: ref.clientKey } : { id: ref.id }), fields, ...(before ? { before } : {}), ...place });
        editsRef.current = next; setEdits(next); setSaveError(null);
        writeChoices(owner.current, generationId, { groups: selectedRef.current, seen: seenRef.current, edits: next, setAside: setAsideRef.current });
    }, [generationId]);
    const changeRows = React.useCallback((change: ReviewEdit) => {
        if (generationId === undefined || attachmentRef.current?.replay) return;
        let next = [...editsRef.current];
        const replace = (entry: ReviewEdit) => {
            const key = reviewEditKey(entry.kind, entry);
            const index = next.findIndex(edit => reviewEditKey(edit.kind, edit) === key);
            if (index >= 0) next[index] = entry; else next.push(entry);
        };
        if (change.action === 'delete') {
            // The removed row takes no other edit (a row the teacher added was never saved: its addition goes, and nothing
            // is removed), and it leaves every order of its list, so no order names a row the save removes; an order left
            // with one row orders nothing.
            const rowKey = reviewEditKey(change.kind, { id: change.id, clientKey: change.clientKey });
            const added = next.some(edit => edit.action === 'create' && reviewEditKey(edit.kind, edit) === rowKey);
            const sameRow = (row: ReviewRowRef) => change.clientKey ? row.clientKey === change.clientKey : !row.clientKey && row.id === change.id;
            next = next.filter(edit => (edit.action !== undefined && edit.action !== 'create') || reviewEditKey(edit.kind, edit) !== rowKey)
                .map(edit => edit.action === 'order' && edit.kind === change.kind ? { ...edit, order: (edit.order ?? []).filter(row => !sameRow(row)) } : edit)
                .filter(edit => edit.action !== 'order' || (edit.order ?? []).length >= 2);
            if (!added) replace(change);
        } else if (change.action === 'create') {
            // A new row goes after the others, as the save writes it: also in an order already chosen for its list.
            const parent = change.parent;
            const sameParent = (edit: ReviewEdit) => edit.action === 'order' && edit.kind === change.kind && edit.parent?.kind === parent?.kind
                && (parent?.clientKey ? edit.parent?.clientKey === parent.clientKey : !edit.parent?.clientKey && edit.parent?.id === parent?.id);
            next = next.map(edit => sameParent(edit) ? { ...edit, order: [...(edit.order ?? []), { clientKey: change.clientKey }] } : edit);
            replace(change);
        } else if (change.action === 'order') {
            // A list put in order again keeps the rows the earlier order named that this one leaves out (not on screen
            // while a projection is prepared, say), each after the row it followed there.
            const key = reviewEditKey(change.kind, change);
            const earlier = next.find(edit => reviewEditKey(edit.kind, edit) === key);
            replace(earlier ? { ...change, order: mergedOrder(earlier.order ?? [], change.order ?? [], Array.isArray(change.before?.order) ? change.before!.order as unknown[] : undefined) } : change);
        } else replace(change);
        editsRef.current = next; setEdits(next); setSaveError(null);
        writeChoices(owner.current, generationId, { groups: selectedRef.current, seen: seenRef.current, edits: next, setAside: setAsideRef.current });
    }, [generationId]);
    /** A selection change, asking first when it would replace edited fields or drop edits to a row it adds or removes. */
    const requestChoice = React.useCallback((next: Set<string>, then?: () => void) => {
        if (!preview) return;
        const current = selectedRef.current;
        const added = allStops.filter(stop => next.has(stop.group) && !current.has(stop.group));
        const removed = allStops.filter(stop => !next.has(stop.group) && current.has(stop.group));
        const addedTouches = editsTouchedBy(added, preview, editsRef.current);
        const replaced = addedTouches.flatMap(touch => touch.fields === null ? [] : [{ edit: touch.edit, fields: touch.fields }]);
        // An included removal drops the teacher's edits inside what it removes, rows they added and orders there too, but
        // never their own removal of a row in it (the save passes over a row already gone); a left-out addition drops their
        // edits to the new item and the rows they added, removed or moved in it. Leaving out a removal, or including an
        // addition, keeps every edit.
        const dropped = [...editsTouchedBy(added.filter(stop => REMOVAL_STOP_KINDS.has(stop.kind)), preview, editsRef.current)
                .filter(touch => touch.fields === null && touch.edit.action !== 'delete' && (!touch.edit.clientKey || touch.edit.action === 'create')),
            ...editsTouchedBy(removed.filter(stop => !REMOVAL_STOP_KINDS.has(stop.kind)), preview, editsRef.current)
                .filter(touch => touch.fields === null && (touch.edit.clientKey || touch.edit.action))]
            .map(touch => touch.edit).filter((edit, index, all) => all.indexOf(edit) === index);
        if (!replaced.length && !dropped.length) { choose(next); then?.(); return; }
        setPendingChoice({ next, replaced, dropped, then });
    }, [preview, allStops, choose]);
    const resolveChoice = React.useCallback((answer: 'keep' | 'ai' | 'cancel') => {
        const pending = pendingChoice;
        setPendingChoice(null);
        if (!pending || answer === 'cancel') return;
        let nextEdits = editsRef.current.filter(edit => !pending.dropped.includes(edit));
        if (answer === 'ai') {
            // The AI value replaces the teacher's for those fields; an edit left with no fields goes.
            nextEdits = nextEdits.flatMap(edit => {
                const replaced = pending.replaced.filter(item => item.edit === edit).flatMap(item => item.fields);
                if (!replaced.length) return [edit];
                const fields = Object.fromEntries(Object.entries(edit.fields).filter(([field]) => !replaced.includes(field)));
                const before = edit.before ? Object.fromEntries(Object.entries(edit.before).filter(([field]) => !replaced.includes(field))) : undefined;
                return Object.keys(fields).length ? [{ ...edit, fields, ...(before ? { before } : {}) }] : [];
            });
        }
        choose(pending.next, nextEdits);
        pending.then?.();
    }, [pendingChoice, choose]);
    const setIncluded = React.useCallback((stop: IVerbaScholaAiReviewChange, include: boolean) => requestChoice(toggled(selected, stop, include)),
        [selected, requestChoice, toggled]);
    const setManyIncluded = React.useCallback((changes: { stop: IVerbaScholaAiReviewChange; include: boolean }[], then?: () => void) =>
        requestChoice(changes.reduce((next, change) => toggled(next, change.stop, change.include), selected), then), [selected, requestChoice, toggled]);
    const includeAll = React.useCallback(() => requestChoice(new Set(allStops.map(stop => stop.group))), [allStops, requestChoice]);
    const clearSelection = React.useCallback(() => requestChoice(new Set()), [requestChoice]);
    const editsKey = React.useMemo(() => JSON.stringify(edits), [edits]);
    const edited = React.useCallback((stop: IVerbaScholaAiReviewChange) => Boolean(preview) && stopTouches(stop, preview!).some(target =>
        edits.some(edit => touchesEdit(target, edit))), [preview, edits]);
    const currentEdited = React.useMemo(() => {
        if (!current || !preview) return false;
        // A rewrite is edited when either the saved item or the new one is.
        const halves = [current, ...allStops.filter(other => other !== current && current.pair_key && other.pair_key === current.pair_key && rewriteHalves.has(other.kind))];
        return halves.some(stop => stopTouches(stop, preview).some(target => edits.some(edit => touchesEdit(target, edit))));
    }, [current, preview, edits, allStops]);
    const included = React.useCallback((stop: IVerbaScholaAiReviewChange) => selected.has(stop.group), [selected]);
    const shows = React.useCallback((stop: IVerbaScholaAiReviewChange) => version === 'proposal' || (version === 'yours' && selected.has(stop.group)),
        [version, selected]);
    const savingRef = React.useRef(false);
    const describe = React.useMemo(() => describeError ?? ((caught: unknown) => String(caught)), [describeError]);
    const rate = React.useCallback<RateResponse>(async (rating, reason_code, note) => {
        if (!preview || lessonId === undefined) return 'The proposal is no longer open.';
        const messageId = preview.review_context?.message_id;
        try {
            await VerbaScholaApi.submitLessonAiFeedback(lessonId, { ...(messageId ? { message_id: messageId } : { generation_id: preview.generation.id }), rating, reason_code, note });
            return null;
        } catch (caught) { return describe(caught); }
    }, [preview, lessonId, describe]);
    /**
     * Writes edits that wait after the AI changes were saved (or none kept), keeping the stored record current: it
     * shrinks as each edit is written and holds what failed or conflicted until the teacher retries or discards it.
     */
    // Edits being written after the AI save (a save, Save my edits only or Try again); leaving stops them part way.
    const writingRef = React.useRef(0);
    const writeEdits = React.useCallback(async (record: UnsavedRecord) => {
        if (lessonId === undefined) return;
        // Marked as being written, so another tab neither offers nor removes it meanwhile.
        ownSaves.current.add(record.token);
        let current: UnsavedRecord = { ...record, awaiting: undefined, writing: true };
        writeUnsaved(owner.current, lessonId, current);
        writingRef.current += 1;
        try {
            const result = await saveReviewEdits(lessonId, record.generationId, record.edits, describe, written => {
                current = { ...current, edits: current.edits.filter(edit => edit !== written) };
                writeUnsaved(owner.current, lessonId, current);
            });
            writeUnsaved(owner.current, lessonId, { ...current, writing: undefined, edits: result.unsaved, error: result.error ?? '',
                conflicts: [...record.conflicts, ...result.conflicts] });
        } finally {
            writingRef.current -= 1;
            ownSaves.current.delete(record.token);
            setRecords(readUnsavedRecords(owner.current, lessonId));
        }
        editsSaved.current?.();
    }, [lessonId, describe]);
    /** Why a new save must wait, read fresh from storage: another tab may have just stored edits of its own. */
    const earlierSaveWaiting = React.useCallback((): string | null => {
        if (lessonId === undefined) return null;
        const stored = readUnsavedRecords(owner.current, lessonId).filter(record => !ownSaves.current.has(record.token));
        if (!stored.length) return null;
        refreshUnsaved();
        return stored.some(offered) ? EARLIER_EDITS_WAITING : SAVE_UNDER_WAY;
    }, [lessonId, refreshUnsaved, offered]);
    /**
     * Settles earlier saves whose outcome was not known (see ``settleRecord``) before a new save, so a retry never applies
     * twice; one the server cannot be asked about yet, or another tab is still making, makes the new save wait.
     */
    const settleEarlierAttempts = React.useCallback(async () => {
        if (lessonId === undefined) return;
        const earlier = readUnsavedRecords(owner.current, lessonId)
            .filter(record => record.awaiting && !underWayInAnotherTab(record) && !ownSaves.current.has(record.token));
        for (const record of earlier) onSettled(record, await settleRecord(owner.current, lessonId, record));
        if (earlier.length) setRecords(readUnsavedRecords(owner.current, lessonId));
    }, [lessonId, onSettled]);
    const leaveWarning = React.useCallback((): string | null => {
        if (savingRef.current || writingRef.current) return 'Your version is still being saved. Leave this page anyway?';
        if (unkeptRef.current.size) return 'An open edit in the review is not kept in Your version yet. Leave this page and discard it?';
        if (unstoredRecords.size) return 'This browser could not store some of your unsaved edits, so leaving this page loses them. Leave anyway?';
        if (!reviewStoreWorks && attachedId.current !== null && (selectedRef.current.size || editsRef.current.length))
            return 'This browser could not store your review, so leaving this page loses your choices and edits. Leave anyway?';
        return null;
    }, []);
    const refreshWarning = React.useCallback((): string | null => {
        if (savingRef.current || writingRef.current) return 'Your version is still being saved. Refresh anyway?';
        if (unkeptRef.current.size) return 'An open edit in the review is not kept in Your version yet. Refresh and discard it?';
        return null;
    }, []);
    // Closing or reloading the tab asks the browser's own question when leaveWarning has a reason.
    React.useEffect(() => {
        const protect = (event: BeforeUnloadEvent) => { if (leaveWarning()) { event.preventDefault(); event.returnValue = ''; } };
        window.addEventListener('beforeunload', protect);
        return () => window.removeEventListener('beforeunload', protect);
    }, [leaveWarning]);
    const [rejected, setRejected] = React.useState<{ edit: ReviewEdit; reason: string; current?: Record<string, unknown>; message?: string }[]>([]);
    /**
     * One save of Your version: the chosen AI changes (``keys``; none for an edits-only save) and the teacher's edits, in a
     * single Apply sent as this save's own attempt, which the server writes in one transaction or not at all. The attempt
     * is stored before it, so a reload at any point still finds and settles it (``awaiting`` until then).
     */
    const submit = React.useCallback(async (keys: string[], pending: PendingReviewEdit[]) => {
        if (!attachment) return;
        const id = attachment.preview.generation.id;
        const record = lessonId !== undefined ? newUnsavedRecord({ proposalId: id, generationId: id, edits: pending, awaiting: 'applied', atomic: true }) : null;
        const token = record?.token ?? crypto.randomUUID();
        const drop = () => { if (record) { removeUnsaved(owner.current, lessonId!, record.token); ownSaves.current.delete(record.token); } };
        if (record) { ownSaves.current.add(record.token); writeUnsaved(owner.current, lessonId!, record); }
        setRejected([]);
        const failure: ReviewSaveFailure | null = await attachment.apply(keys, token, serverEdits(pending));
        // Any failure but a definite "saved from another choice" is settled by the server for good: a lost answer may
        // hide a save that happened, and a refusal may come from the proposal having been dismissed elsewhere.
        const outcome: AttemptOutcome = failure === null ? 'applied' : failure.savedElsewhere ? 'savedElsewhere'
            : lessonId !== undefined ? await attemptOutcome(lessonId, id, token) : 'open';
        const closed = closedElsewhereAs(outcome);
        if (outcome === 'applied') {
            // Saved with its edits: nothing is left to write, and nothing more is projected while the review closes.
            setSavedProposal(id);
            drop(); forgetChoices(owner.current, id);
            editsSaved.current?.();
            // Saved although the save reported a failure: the copilot still shows the proposal, so the review closes here.
            if (failure !== null) attachment.close();
            return;
        }
        if (closed) {
            // Another tab or teacher saved (or closed) the proposal first, from another choice: this save was not made.
            // The edits are kept as unsaved edits, offered above the page also after a reload, where each is written only
            // while its field still holds what the review showed.
            forgetChoices(owner.current, id);
            if (record && pending.length) {
                ownSaves.current.delete(record.token);
                writeUnsaved(owner.current, lessonId!, { ...record, awaiting: undefined, atomic: undefined, error: SAVED_ELSEWHERE });
                setRecords(readUnsavedRecords(owner.current, lessonId!));
                attachment.close();
            } else { drop(); setClosedElsewhere(closed); setSaveError(SAVED_ELSEWHERE_NO_EDITS); }
            return;
        }
        if (outcome === 'unknown') {
            // The server could not be asked: the record waits, and is settled again until it answers.
            if (record) { ownSaves.current.delete(record.token); setRecords(readUnsavedRecords(owner.current, lessonId!)); }
            setSaveError(`${failure!.message} Whether it was saved is being checked, and saving waits until it is known. Your choices and edits stay here; if it was saved, this review closes.`);
            if (failure!.stale) setStaleFailure(true);
            return;
        }
        // Not saved, none of it: the choices and edits stay in the review. Edits that refused it are listed to redo or leave out.
        drop();
        if (failure!.rejected?.length) {
            setRejected(failure!.rejected.flatMap(item => pending[item.index] ? [{ edit: pending[item.index], reason: item.reason,
                ...(item.current ? { current: item.current } : {}), ...(item.message ? { message: item.message } : {}) }] : []));
        }
        // A proposal found out of date stays so (attach clears it for another proposal): a later refusal of an edits-only
        // save, for an edit that changed meanwhile, says nothing about the AI changes.
        setSaveError(failure!.message);
        if (failure!.stale) setStaleFailure(true);
    }, [attachment, lessonId]);
    const save = React.useCallback(async (dismissReason?: string) => {
        // Nothing chosen and no edit still ends the review (Keep the saved lesson): the proposal is dismissed below.
        if (!attachment || savingRef.current) return;
        savingRef.current = true; setSaving(true); setSaveError(null);
        await settleEarlierAttempts();
        // Every save checks, not just the bar's button: one save's unsaved edits are never replaced by another's.
        const waiting = earlierSaveWaiting();
        if (waiting) { savingRef.current = false; setSaving(false); setSaveError(waiting); return; }
        const preview = attachment.preview;
        // The edits this choice of changes can write (the confirmation listed the rest); each is written only while its
        // fields hold what the review expects: the saved values, or what the chosen AI changes write.
        const pending: PendingReviewEdit[] = planReviewSave(editsRef.current, selectedStops, preview).send
            .map(edit => ({ ...edit, expected: expectedBeforeEdit(edit, selectedStops, preview) }));
        try {
            if (!selectedStops.length && !pending.length) {
                // Nothing of Your version to save: only the proposal is closed.
                forgetChoices(owner.current, preview.generation.id);
                const message = attachment.dismiss ? await attachment.dismiss(dismissReason) : 'This proposal cannot be closed from here.';
                if (message !== null) setSaveError(message);
                return;
            }
            await submit(selectedStops.map(stop => stop.key), pending);
        } finally { savingRef.current = false; setSaving(false); }
    }, [attachment, selectedStops, earlierSaveWaiting, settleEarlierAttempts, submit]);
    const outdated = Boolean(preview && (staleFailure || savedElsewhere || (!preview.can_apply && /^(stale|proposal_stale)/.test(preview.apply_block_reason ?? ''))));
    // Edits waiting from an earlier save are saved or discarded first, so they are written in the order they were made.
    const earlierUnsaved = held ? HELD_BY_SAVE : shownRecords.length ? EARLIER_EDITS_WAITING : underWayElsewhere ? SAVE_UNDER_WAY : null;
    // The AI changes can no longer be saved; the teacher's edits to saved rows can, in one save that also closes the proposal.
    const saveEditsOnly = React.useCallback(async () => {
        if (!attachment || savingRef.current || lessonId === undefined) return;
        // Every edit the current lesson can take, rows the teacher added under saved activities too; the bar listed the
        // edits to items only this proposal adds before the save.
        const pending: PendingReviewEdit[] = planReviewSave(editsRef.current, [], attachment.preview).send
            .map(edit => ({ ...edit, expected: expectedBeforeEdit(edit, [], attachment.preview) }));
        if (!pending.length) { attachment.close(); return; }
        savingRef.current = true; setSaving(true); setSaveError(null);
        await settleEarlierAttempts();
        const waiting = earlierSaveWaiting();
        if (waiting) { savingRef.current = false; setSaving(false); setSaveError(waiting); return; }
        try { await submit([], pending); } finally { savingRef.current = false; setSaving(false); }
    }, [attachment, lessonId, earlierSaveWaiting, settleEarlierAttempts, submit]);
    /** Leaves edits out of Your version (ones its projection could not show, which a save would refuse). */
    const dropEdits = React.useCallback((leaving: ReviewEdit[]) => {
        choose(selectedRef.current, withoutEdits(editsRef.current, new Set(leaving.map(edit => reviewEditKey(edit.kind, edit)))));
    }, [choose]);
    const dropRejected = React.useCallback(() => {
        const leaving = new Set(rejected.map(item => reviewEditKey(item.edit.kind, item.edit)));
        setRejected([]); setSaveError(null);
        choose(selectedRef.current, withoutEdits(editsRef.current, leaving));
    }, [rejected, choose]);
    const keepRejected = React.useCallback(() => {
        const seen = new Map(rejected.filter(item => item.reason === 'changed' && item.current).map(item => [reviewEditKey(item.edit.kind, item.edit), item.current!]));
        if (!seen.size) return;
        // The values the teacher has now seen become the saved values the edit was made over (an order: the list's order).
        const next = editsRef.current.map(edit => {
            const current = seen.get(reviewEditKey(edit.kind, edit));
            if (!current) return edit;
            // An order then names only rows the list holds now (rows the teacher added still go by draft key).
            const listed = Array.isArray(current.order) ? current.order as unknown[] : [];
            return edit.action === 'order' ? { ...edit, before: { order: current.order }, order: (edit.order ?? []).filter(ref => ref.clientKey || listed.includes(ref.id)) }
                : { ...edit, before: { ...edit.before, ...current } };
        });
        setRejected(items => items.filter(item => !(item.reason === 'changed' && item.current))); setSaveError(null);
        choose(selectedRef.current, next);
        // The page reloads the lesson, so it shows what the edits now replace.
        editsSaved.current?.();
    }, [rejected, choose]);
    const unsavedEdits = React.useMemo<UnsavedReviewEdits | null>(() => {
        if (!shownRecords.length || lessonId === undefined) return null;
        // Added, removed and moved rows are never written by Try again (it lists them), so they neither count as edits to
        // retry nor as fields the lesson changed: stored records keep them where they were, and they are told apart here.
        const retryable = (record: UnsavedRecord) => record.edits.filter(edit => !edit.action);
        return {
            edits: shownRecords.flatMap(retryable), conflicts: shownRecords.flatMap(record => record.conflicts.filter(edit => !edit.action)),
            structural: shownRecords.flatMap(record => [...record.edits, ...record.conflicts].filter(edit => edit.action)), retrying,
            unstored: shownRecords.some(record => record.unstored),
            error: [...new Set(shownRecords.flatMap(record => retryable(record).length && record.error ? [record.error] : []))].join(' ') || 'Saving was interrupted.',
            // The stored edits keep the values they were checked against, including what the AI changes wrote; oldest save first.
            retry: () => {
                setRetrying(true);
                void (async () => { for (const record of shownRecords) if (retryable(record).length) await writeEdits(record); })().finally(() => setRetrying(false));
            },
            discard: () => {
                for (const record of shownRecords) removeUnsaved(owner.current, lessonId, record.token);
                setRecords(readUnsavedRecords(owner.current, lessonId));
            },
        };
    }, [shownRecords, lessonId, retrying, writeEdits]);
    const dismissSetAside = React.useCallback(() => {
        setAsideRef.current = []; setSetAside([]);
        if (generationId !== undefined) writeChoices(owner.current, generationId, { groups: selectedRef.current, seen: seenRef.current, edits: editsRef.current, setAside: [] });
    }, [generationId]);
    const label = React.useCallback((stop: IVerbaScholaAiReviewChange) => preview ? stopLabel(stop, preview, names) : '', [preview, names]);
    const total = listedStops.length;
    const rewritePair = React.useCallback((stop: IVerbaScholaAiReviewChange) => stop.pair_key ? allStops.find(other => other !== stop
        && other.pair_key === stop.pair_key && rewriteHalves.has(other.kind) && removalKinds.has(other.kind) !== removalKinds.has(stop.kind)) ?? null : null, [allStops]);
    const savePlan = React.useMemo<ReviewSavePlan>(() => preview ? planReviewSave(edits, selectedStops, preview) : { send: [], unsaveable: [] },
        [preview, edits, selectedStops]);
    const editsOnlyPlan = React.useMemo<ReviewSavePlan>(() => preview ? planReviewSave(edits, [], preview) : { send: [], unsaveable: [] }, [preview, edits]);
    // An outdated proposal's AI changes cannot be saved, so its Your version is the current lesson with the edits alone.
    const projectionEdits = React.useMemo<PendingReviewEdit[]>(() => {
        if (!preview) return [];
        const chosen = outdated ? [] : selectedStops;
        return (outdated ? editsOnlyPlan : savePlan).send.filter(edit => ACTIVITY_EDIT_KINDS.has(edit.kind))
            .map(edit => ({ ...edit, expected: expectedBeforeEdit(edit, chosen, preview) }));
    }, [preview, outdated, selectedStops, savePlan, editsOnlyPlan]);
    const session = React.useMemo<ProposalReviewSession | null>(() => enabled && attachment && preview ? {
        page, preview, attachment, stops, otherStops, pageOf, index, position, total, current, linked, version, selectedStops, selectionKey,
        reviewedCount, unreviewedSelected, included, shows, focusKey, saving, saveError, label, replay: Boolean(attachment.replay),
        setVersion: value => guarded(() => setVersion(value)),
        setIncluded: (stop, include) => { if (!held && !attachment.replay) guarded(() => setIncluded(stop, include)); },
        setManyIncluded: (changes, advance) => {
            if (held || attachment.replay) return;
            const declined = changes.find(change => change.stop.kind === 'exercise_addition' && !change.include)?.stop.client_key;
            guarded(() => setManyIncluded(changes, advance ? () => moveOn(declined ?? undefined) : undefined));
        },
        includeAll: () => { if (!held && !attachment.replay) guarded(includeAll); }, clearSelection: () => { if (!held && !attachment.replay) guarded(clearSelection); },
        go: delta => guarded(() => go(delta)), goTo: key => guarded(() => goTo(key)),
        // Keeping refused edits reloads the lesson, so an editor's text not kept yet asks first, as any redraw does.
        edits, editsKey, editable: version === 'yours' && !saving && !held && !attachment.replay, held, rejectedEdits: rejected, dropRejected,
        keepRejected: () => guarded(keepRejected), dropEdits,
        savePlan, editsOnlyPlan, projectionEdits, saved: savedProposal === preview.generation.id, rewritePair, chosenStops,
        editRow, changeRows, currentEdited, pendingChoice, resolveChoice, carriedOver,
        outdated, savedElsewhere, closedElsewhere, saveEditsOnly: () => guarded(() => { void saveEditsOnly(); }), orderedStops, reviewed, edited, nextUnreviewed,
        goToStop: stop => guarded(() => goToStop(stop)), pendingLeave: Boolean(pendingLeave), resolveLeave, earlierUnsaved, guard: guarded,
        setAside, dismissSetAside,
        save: dismissReason => guarded(() => { void save(dismissReason); }), moveOn,
        close: () => guarded(() => attachment.close()),
        showFindings: canShowFindings ? () => showFindings(preview.generation.id) : null,
        showComposer: canShowFindings ? showComposer : null, chat: attachment.chat, rate,
    } : null, [enabled, attachment, showFindings, showComposer, rate, canShowFindings, preview, page, stops, otherStops, pageOf, index, position, total, current, linked, version, selectedStops,
        selectionKey, reviewedCount, unreviewedSelected, included, shows, focusKey, saving, saveError, setVersion, setIncluded, setManyIncluded, includeAll, clearSelection, go, goTo, label,
        edits, editsKey, editRow, changeRows, currentEdited, pendingChoice, resolveChoice, carriedOver, outdated, savedElsewhere, closedElsewhere, saveEditsOnly, orderedStops, reviewed, edited, nextUnreviewed, goToStop, save, moveOn,
        guarded, pendingLeave, resolveLeave, earlierUnsaved, setAside, dismissSetAside, held, rejected, dropRejected, keepRejected, dropEdits, savePlan, editsOnlyPlan, projectionEdits, savedProposal, rewritePair, chosenStops]);
    const hasDraft = React.useCallback((id: number) => {
        const choices = readChoices(owner.current, id);
        return choices.groups.size > 0 || choices.edits.length > 0;
    }, []);
    // Unstored edits are lost when the page closes, also after the review itself closed.
    const guardsLeave = Boolean(attachment) || saving || retrying || records.some(record => record.unstored);
    return React.useMemo(() => ({ attach, detach, session, openPage: onOpenPage ?? null, unsavedEdits, markUnkept, guardExcept, leaveWarning, refreshWarning, guardsLeave, hasDraft }),
        [attach, detach, session, onOpenPage, unsavedEdits, markUnkept, guardExcept, leaveWarning, refreshWarning, guardsLeave, hasDraft]);
}

/**
 * The review bar's own state (its change list, filter, comparison and open dialogs), kept by the page's review scope:
 * the bar sits in page content that is drawn anew for each version and choice, which would otherwise reset it.
 */
const BarStateContext = React.createContext<{ state: Record<string, unknown>; set: (key: string, value: unknown, initial: unknown) => void } | null>(null);

function useBarState<T>(key: string, initial: T): [T, (value: T | ((current: T) => T)) => void] {
    const scope = React.useContext(BarStateContext);
    const [own, setOwn] = React.useState<T>(initial);
    if (!scope) return [own, setOwn];
    const value = (key in scope.state ? scope.state[key] : initial) as T;
    return [value, next => scope.set(key, next, initial)];
}

export function ProposalReviewScope({ controller, children }: { controller: ProposalReviewController; children: React.ReactNode }) {
    const unsaved = controller.unsavedEdits;
    const [barState, setBarState] = React.useState<Record<string, unknown>>({});
    const barStore = React.useMemo(() => ({ state: barState, set: (key: string, value: unknown, initial: unknown) => setBarState(current => ({ ...current,
        [key]: typeof value === 'function' ? (value as (latest: unknown) => unknown)(key in current ? current[key] : initial) : value })) }), [barState]);
    return <BarStateContext.Provider value={barStore}><ReviewContext.Provider value={controller}>
        {REVIEW_HIGHLIGHT_STYLES}
        {unsaved && <Alert severity="warning" sx={{ mb: 2 }} data-testid="proposal-review-unsaved-edits" action={<Stack direction="row" spacing={1}>
            {unsaved.edits.length > 0 && <Button size="small" disabled={unsaved.retrying} onClick={unsaved.retry}>Try again</Button>}
            <Button size="small" disabled={unsaved.retrying} onClick={unsaved.discard}>{unsaved.edits.length ? 'Discard edits' : 'Dismiss'}</Button>
        </Stack>}>
            {unsaved.edits.length > 0 && <Box>{unsaved.edits.length} of your edits {unsaved.edits.length === 1 ? 'was' : 'were'} not saved: {unsaved.error}</Box>}
            {unsaved.conflicts.length > 0 && <Box>
                {unsaved.conflicts.length} of your edits {unsaved.conflicts.length === 1 ? 'was' : 'were'} not saved, because the lesson changed {unsaved.conflicts.length === 1 ? 'that field' : 'those fields'} since you started reviewing, or the item is not in the lesson (an AI-created item that was never saved). Redo {unsaved.conflicts.length === 1 ? 'it' : 'them'} on the page if still wanted:
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{unsaved.conflicts.map((edit, index) => <li key={`${reviewEditKey(edit.kind, edit)}:${index}`}>{editSummary(edit)}</li>)}</Box>
            </Box>}
            {unsaved.structural.length > 0 && <Box data-testid="proposal-review-unsaved-structure">
                Added, removed or moved answers and lines belonged to the save that was not made: redo {unsaved.structural.length === 1 ? 'it' : 'them'} on the page if still wanted:
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{unsaved.structural.map((edit, index) => <li key={`${reviewEditKey(edit.kind, edit)}:${index}`}>{editSummary(edit)}</li>)}</Box>
            </Box>}
            {unsaved.unstored && <Box data-testid="proposal-review-unstored">This browser could not store them, so they are kept only while this page stays open.</Box>}
        </Alert>}
        <LessonReviewUnkeptContext.Provider value={controller.markUnkept}>
            <ReviewPageLinkContext.Provider value={controller.openPage}>{children}</ReviewPageLinkContext.Provider>
        </LessonReviewUnkeptContext.Provider>
    </ReviewContext.Provider></BarStateContext.Provider>;
}

/** Says that reviewing a proposal again continues the teacher's kept review of it, unless that review is open now. */
export function ReviewDraftNote({ generationId }: { generationId: number }) {
    const registry = React.useContext(ReviewContext);
    if (!registry?.hasDraft(generationId) || registry.session?.preview.generation.id === generationId) return null;
    return <Typography variant="caption" data-testid="proposal-review-draft-note">
        Your review of this proposal is kept in this browser; Review proposal continues it.
    </Typography>;
}

const ReviewPageLinkContext = React.createContext<OpenReviewPage | null>(null);

/** Opens a proposal's review on another builder page, where this page can. */
export function useOpenReviewPage(): OpenReviewPage | null {
    return React.useContext(ReviewPageLinkContext);
}
export const pageNames: Record<ReviewPage, string> = { description: 'Description', plan: 'Lesson plan', exercises: 'Exercises' };
const additionKinds = new Set(['target_addition', 'plan_section_addition', 'generated_material_addition', 'exercise_addition',
    'exercise_addition_step', 'choice_addition', 'speaking_addition', 'typed_addition', 'line_addition', 'step_addition']);
const removalKinds = new Set(['choice_deletion', 'speaking_deletion', 'typed_deletion', 'line_deletion', 'step_deletion',
    'exercise_removal', 'plan_section_removal']);
/** The two halves a rewrite pairs: the saved card or exercise it removes, and the new one it adds. */
const rewriteHalves = new Set(['plan_section_removal', 'plan_section_addition', 'exercise_removal', 'exercise_addition']);

/** The saved items and new items of a replacement shown on each builder page. */
const REPLACEMENT_SETS: Partial<Record<ReviewPage, { removal: string; item: string; within: string[]; one: string; many: string }>> = {
    plan: { removal: 'plan_section_removal', item: 'plan_section_addition', within: [], one: 'plan card', many: 'plan cards' },
    exercises: { removal: 'exercise_removal', item: 'exercise_addition', within: ['exercise_addition_step'], one: 'exercise', many: 'exercises' },
};

/**
 * A replacement (Replace all) chosen as a whole: the saved cards or exercises out and the new ones in, both sets, or
 * the saved set alone. It is the common decision when a set is regenerated, and most new items do not pair with a saved
 * one of the same title; each change can still be chosen on its own below.
 */
function ReplacementSet({ session }: { session: ProposalReviewSession }) {
    const set = REPLACEMENT_SETS[session.page];
    const all = session.preview.review_changes ?? [];
    const removals = set ? all.filter(stop => stop.kind === set.removal) : [];
    if (!set || !removals.length) return null;
    const additions = all.filter(stop => stop.kind === set.item || set.within.includes(stop.kind));
    const newItems = all.filter(stop => stop.kind === set.item);
    const words = (count: number, prefix: string) => `${count} ${prefix} ${count === 1 ? set.one : set.many}`;
    const removed = removals.filter(stop => session.included(stop)).length;
    const added = newItems.filter(stop => session.included(stop)).length;
    // A set counts as chosen whole only with every new item's activities too; one left out makes it a choice of its own.
    const allRemoved = removed === removals.length;
    const allAdded = additions.every(stop => session.included(stop));
    const noneAdded = !additions.some(stop => session.included(stop));
    // Only activities of new items that are kept: a new exercise left out whole leaves out no activity of a kept one.
    const keptNew = new Set(newItems.filter(stop => session.included(stop)).map(stop => stop.client_key));
    const someActivitiesOut = additions.some(stop => set.within.includes(stop.kind) && stop.parent_key !== undefined && stop.parent_key !== null
        && keptNew.has(stop.parent_key) && !session.included(stop));
    const state = allRemoved && allAdded ? 'Your version uses the new set.'
        : !removed && allAdded ? 'Your version keeps both sets.'
            : !removed && noneAdded ? 'Your version keeps the saved set.'
                : `Your version takes out ${removed} of the ${words(removals.length, 'saved')} and adds ${added} of the ${words(newItems.length, 'new')}${someActivitiesOut ? ', some of their activities left out' : ''}.`;
    const choose = (remove: boolean, add: boolean) => session.setManyIncluded([...removals.map(stop => ({ stop, include: remove })), ...additions.map(stop => ({ stop, include: add }))]);
    const options = [['Use the new set', true, true], ['Keep both sets', false, true], ['Keep the saved set', false, false]] as const;
    // A section of the review card under its save row, so it is seen with the rest of the review.
    return <Stack data-testid="proposal-review-replacement" direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 1 }} alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ borderTop: `1px solid ${BORDER}`, pt: 0.75 }}>
        <Typography sx={{ fontFamily: INTER, fontSize: 12.5, color: NAVY, flex: 1, minWidth: 0 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>Replacement: </Box>
            this proposal replaces your {words(removals.length, 'saved')} with {newItems.length ? words(newItems.length, 'new') : 'none'}. {state} Each change can also be chosen on its own.
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap role="group" aria-label="Replacement">
            {options.map(([text, remove, add]) => {
                const current = (remove ? allRemoved : !removed) && (add ? allAdded : noneAdded);
                return <Button key={text} size="small" variant={current ? 'outlined' : 'text'} aria-pressed={current} disabled={session.saving || session.held}
                    onClick={() => choose(remove, add)} sx={{ minHeight: 30, whiteSpace: 'nowrap' }}>{text}</Button>;
            })}
        </Stack>
    </Stack>;
}

/** Edits a save leaves out, listed before it, so the teacher can redo them by hand. */
function UnsaveableEdits({ items, lead }: { items: ReviewSavePlan['unsaveable']; lead: string }) {
    if (!items.length) return null;
    return <Box data-testid="proposal-review-unsaveable" sx={{ mt: 1, fontFamily: INTER, fontSize: 13.5, color: WARNING_TEXT }}>
        {lead}
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{items.map((item, index) => <li key={`${reviewEditKey(item.edit.kind, item.edit)}:${index}`}>
            {item.reason === 'removed_by_ai' ? 'Inside an item the AI removes: ' : 'On an item only this proposal adds: '}{editSummary(item.edit)}
        </li>)}</Box>
    </Box>;
}

/** What the version on screen shows of the current stop, when that is not simply its change; ``replay``: a saved version. */
export function reviewAbsenceNote(stop: IVerbaScholaAiReviewChange | null, version: ReviewVersion, included: boolean, replay = false,
    rewrite?: { pairIncluded: boolean }): string | null {
    if (!stop) return null;
    const addition = additionKinds.has(stop.kind);
    const removal = removalKinds.has(stop.kind);
    if (rewrite) {
        // A rewrite: a saved card or exercise and the new one of its title. The version on screen shows one or both.
        const noun = stop.kind.startsWith('exercise') ? 'exercise' : 'card';
        if (version === 'before') return addition ? `New in the proposal: it replaces the saved ${noun} of the same title, highlighted here. Switch to AI proposal to see the new one.`
            : removal ? `This is the saved ${noun}, highlighted here. The proposal replaces it with a new one of the same title: switch to AI proposal to see it.` : null;
        if (version === 'proposal') return removal ? `The proposal replaces this saved ${noun} with a new one of the same title, highlighted here. Switch to Before to see the saved one.` : null;
        const [removed, added] = removal ? [included, rewrite.pairIncluded] : [rewrite.pairIncluded, included];
        const where = replay ? 'this save' : 'your version';
        if (removed && added) return `${replay ? 'This save replaced' : 'Your version replaces'} the saved ${noun} with the new one of the same title, highlighted here. Switch to Before to see the saved one.`;
        if (removed) return `${replay ? 'This save removed' : 'Your version removes'} the saved ${noun}, with no new one in its place. Switch to Before to see it.`;
        if (added) return `${replay ? 'This save kept' : 'Your version keeps'} the saved ${noun} and ${replay ? 'added' : 'adds'} the new one of the same title.`;
        return `${replay ? 'This save kept' : 'Your version keeps'} the saved ${noun}, highlighted here; the new one is left out of ${where}.`;
    }
    if (replay && version === 'yours' && !included) return removal ? 'Left out of this save: the saved item stayed.' : 'Left out of this save.';
    if (version === 'before') return addition ? 'New in the proposal: the saved lesson does not have it. Switch to AI proposal to see it.' : null;
    if (version === 'proposal') return removal ? 'Removed by the proposal. Switch to Before to see what it removes.' : null;
    if (!included) return addition ? 'Not in your version: include it to add it.'
        : removal ? 'Not in your version: the saved item stays.' : 'Not in your version: the saved content stays.';
    return removal ? 'Removed in your version. Switch to Before to see what it removes.' : null;
}

/** A rewrite's state in Your version (or as saved), from whether its removal of the saved item and its new item are included. */
function rewriteStatus(removed: boolean, added: boolean, replay = false): string {
    const state = removed && added ? 'Uses the new one' : added ? 'Keeps both' : removed ? 'Deletes the saved one' : 'Keeps the saved one';
    return replay ? `${state} in this save` : `${state} in your version`;
}

const versionLabels: Record<ReviewVersion, string> = { before: 'Before', proposal: 'AI proposal', yours: 'Your version' };
// A saved version shown again: Your version is what was saved.
const replayLabels: Record<ReviewVersion, string> = { before: 'Before', proposal: 'AI proposal', yours: 'Saved version' };

type ChangeFilter = 'all' | 'included' | 'left_out' | 'not_reviewed' | 'edited';
const changeFilters: [ChangeFilter, string][] = [['all', 'All'], ['included', 'Included'], ['left_out', 'Left out'], ['not_reviewed', 'Not reviewed'], ['edited', 'Edited']];

/** A family of changes chosen together from the list: a new exercise with its activities, a saved exercise's changes, or a rewrite. */
interface ChangeFamily { key: string; label: string; stops: IVerbaScholaAiReviewChange[] }

/** The proposal's changes in review order, those of one family together (a family of one is listed on its own). */
export function changeFamilies(stops: IVerbaScholaAiReviewChange[], preview: IVerbaScholaAiPreview, label: (stop: IVerbaScholaAiReviewChange) => string): (ChangeFamily | IVerbaScholaAiReviewChange)[] {
    const all = preview.review_changes ?? stops;
    const rewriteOf = new Map(all.filter(stop => stop.kind === 'exercise_addition' && stop.pair_key && stop.client_key).map(stop => [stop.client_key!, stop.pair_key!]));
    const familyOf = (stop: IVerbaScholaAiReviewChange): string | null => stop.pair_key ? stop.pair_key
        : stop.kind === 'exercise_addition' ? `new:${stop.client_key}`
            : stop.kind === 'exercise_addition_step' ? rewriteOf.get(stop.parent_key ?? '') ?? `new:${stop.parent_key}`
                : stop.exercise_id && stop.kind !== 'exercise_removal' ? `exercise:${stop.exercise_id}` : null;
    const members = new Map<string, IVerbaScholaAiReviewChange[]>();
    for (const stop of stops) { const key = familyOf(stop); if (key) members.set(key, [...(members.get(key) ?? []), stop]); }
    const title = (key: string, family: IVerbaScholaAiReviewChange[]) => {
        // A rewrite's new item may be listed with its saved one rather than on its own.
        const addition = family.find(stop => stop.kind === 'exercise_addition' || stop.kind === 'plan_section_addition')
            ?? all.find(stop => (stop.kind === 'exercise_addition' || stop.kind === 'plan_section_addition') && stop.pair_key && stop.pair_key === key);
        const named = addition && (addition.kind === 'exercise_addition' ? preview.exercise_additions?.find(row => row.client_key === addition.client_key)?.title
            : preview.plan_section_additions?.find(row => row.client_key === addition.client_key)?.title);
        if (key.startsWith('rewrite:')) return `Rewrite of ${named ?? 'a saved item'} with its new activities`;
        if (key.startsWith('new:')) return `New exercise: ${named ?? 'Exercise'}`;
        // A saved exercise: its changes are listed under it, named as their first change names it.
        return `Changes to ${label(family[0]).split(' · ')[0]}`;
    };
    const result: (ChangeFamily | IVerbaScholaAiReviewChange)[] = [];
    const listed = new Set<string>();
    for (const stop of stops) {
        const key = familyOf(stop);
        const family = key ? members.get(key)! : [];
        if (!key || family.length < 2) { result.push(stop); continue; }
        if (listed.has(key)) continue;
        listed.add(key);
        result.push({ key, label: title(key, family), stops: family });
    }
    return result;
}

/** A checkbox for several changes: ticked when all are included, partly chosen when only some are. */
function FamilyCheckbox({ session, stops, label }: { session: ProposalReviewSession; stops: IVerbaScholaAiReviewChange[]; label: string }) {
    const count = stops.filter(stop => session.included(stop)).length;
    return <Checkbox size="small" checked={count === stops.length} indeterminate={count > 0 && count < stops.length}
        disabled={session.saving || session.held || session.replay} inputProps={{ 'aria-label': `Include ${label}` }}
        onChange={() => session.setManyIncluded(stops.map(stop => ({ stop, include: count < stops.length })))} sx={{ p: 0.5 }} />;
}

/** Every change of the proposal, filtered by the teacher's choices; choosing one shows it, on its own page. */
function ReviewChangeList({ session }: { session: ProposalReviewSession }) {
    const [filter, setFilter] = useBarState<ChangeFilter>(`${session.preview.generation.id}:filter`, 'all');
    // A rewrite is listed once, with its new item: included when either half is, edited when either is.
    const halves = (stop: IVerbaScholaAiReviewChange) => { const pair = session.rewritePair(stop); return pair ? [stop, pair] : [stop]; };
    const included = (stop: IVerbaScholaAiReviewChange) => halves(stop).some(half => session.included(half));
    const edited = (stop: IVerbaScholaAiReviewChange) => halves(stop).some(half => session.edited(half));
    const matches = (stop: IVerbaScholaAiReviewChange) => filter === 'all' || (filter === 'included' && included(stop))
        || (filter === 'left_out' && !included(stop)) || (filter === 'not_reviewed' && !session.reviewed(stop)) || (filter === 'edited' && edited(stop));
    const shown = session.orderedStops.filter(matches);
    const entries = changeFamilies(shown, session.preview, session.label);
    const row = (stop: IVerbaScholaAiReviewChange, nested: boolean) => <li key={stop.key}>
        <Stack direction="row" alignItems="center" sx={{ pl: nested ? 3 : 0 }}>
            {session.rewritePair(stop) ? <FamilyCheckbox session={session} stops={halves(stop)} label={session.label(stop)} />
                : <Checkbox size="small" checked={session.included(stop)} disabled={session.saving || session.held || session.replay} sx={{ p: 0.5 }}
                    inputProps={{ 'aria-label': `Include ${session.label(stop)}` }} onChange={event => session.setIncluded(stop, event.target.checked)} />}
            <Button fullWidth onClick={() => session.goToStop(stop)} aria-current={session.current?.key === stop.key ? 'true' : undefined}
                disabled={session.replay && session.pageOf(stop) !== session.page}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', fontFamily: INTER, fontSize: 12.5, color: NAVY, py: 0.5,
                    backgroundColor: session.current?.key === stop.key ? SOFT_BLUE : undefined }}>
                <Box component="span" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{session.label(stop)}</Box>
                <Box component="span" sx={{ ml: 1, whiteSpace: 'nowrap', color: SLATE, fontSize: 11.5 }}>
                    {pageNames[session.pageOf(stop)]} · {session.rewritePair(stop) ? rewriteStatus(session.included(stop), session.included(session.rewritePair(stop)!), session.replay).replace(/ in (your version|this save)$/, '')
                        : session.included(stop) ? 'Included' : 'Left out'}{session.reviewed(stop) ? '' : ' · Not reviewed'}{edited(stop) ? ' · Edited' : ''}
                </Box>
            </Button>
        </Stack>
    </li>;
    const all = session.orderedStops;
    return <Box data-testid="proposal-review-change-list" sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1 }}>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" role="group" aria-label="Show changes">
            {changeFilters.map(([value, text]) => <Button key={value} size="small" aria-pressed={filter === value} variant={filter === value ? 'contained' : 'text'}
                onClick={() => setFilter(value)} sx={{ minHeight: 30, textTransform: 'none' }}>
                {text} ({session.orderedStops.filter(stop => value === 'all' || (value === 'included' && included(stop)) || (value === 'left_out' && !included(stop))
                    || (value === 'not_reviewed' && !session.reviewed(stop)) || (value === 'edited' && edited(stop))).length})
            </Button>)}
        </Stack>
        {/* The whole proposal, then each family: a ticked box includes every change under it, a partly filled one some. */}
        <Stack direction="row" alignItems="center" sx={{ mt: 0.75 }} data-testid="proposal-review-list-all">
            <FamilyCheckbox session={session} stops={all.flatMap(halves)} label="all changes" />
            <Typography sx={{ fontFamily: INTER, fontSize: 12.5, fontWeight: 700, color: NAVY }}>All changes ({all.filter(included).length} of {all.length} included)</Typography>
        </Stack>
        <Box component="ol" sx={{ m: 0, mt: 0.5, p: 0, listStyle: 'none', maxHeight: 280, overflowY: 'auto' }}>
            {entries.map(entry => 'stops' in entry ? <li key={entry.key} data-testid="proposal-review-family">
                <Stack direction="row" alignItems="center">
                    <FamilyCheckbox session={session} stops={entry.stops.flatMap(halves)} label={entry.label} />
                    <Typography sx={{ fontFamily: INTER, fontSize: 12.5, fontWeight: 700, color: NAVY, overflowWrap: 'anywhere' }}>{entry.label}</Typography>
                </Stack>
                <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>{entry.stops.map(stop => row(stop, true))}</Box>
            </li> : row(entry, false))}
            {!shown.length && <li><Typography sx={{ fontFamily: INTER, fontSize: 12.5, color: SLATE, p: 1 }}>No changes match.</Typography></li>}
        </Box>
    </Box>;
}

/** The current change's saved and proposed values: inline when short, a highlighted text comparison on request when long. */
function ReviewComparison({ rows, stateKey }: { rows: StopComparison[]; stateKey: string }) {
    const [open, setOpen] = useBarState(`${stateKey}:compare`, false);
    const note = { fontFamily: INTER, fontSize: 12.5, overflowWrap: 'anywhere' as const };
    if (rows.every(row => row.before.length + row.after.length <= 120 && !/\n/.test(row.before + row.after))) {
        return <Stack data-testid="proposal-review-comparison" spacing={0.25} sx={{ textAlign: 'center' }}>
            {rows.map(row => <Typography key={row.label} sx={{ ...note, color: SLATE }}>
                {rows.length > 1 ? `${row.label}: ` : ''}<Box component="span" sx={{ textDecoration: 'line-through' }}>{row.before}</Box>
                {' → '}<Box component="span" sx={{ fontWeight: 700, color: NAVY }}>{row.after}</Box>
            </Typography>)}
        </Stack>;
    }
    return <Box data-testid="proposal-review-comparison" sx={{ textAlign: 'center' }}>
        <Button size="small" aria-expanded={open} onClick={() => setOpen(value => !value)}>{open ? 'Hide the comparison' : 'Compare saved and proposed text'}</Button>
        {open && <Box sx={{ textAlign: 'left', maxHeight: 220, overflowY: 'auto', '& .MuiTypography-root': { fontSize: 13 } }}>
            {rows.map(row => <Box key={row.label}>{rows.length > 1 && <Typography sx={{ fontWeight: 700 }}>{row.label}</Typography>}
                <ProposalTextDiff before={row.before} after={row.after} /></Box>)}
        </Box>}
    </Box>;
}

/** Sticky controls for the page review: the three versions, the change navigator, the teacher's choices and Save. */
/** Where the teacher moved the review bar to (it starts in its place above the page), kept for this tab. */
type BarPosition = { left: number; top: number; width: number };
const BAR_POSITION_KEY = 'vs-proposal-review-bar-position';

function readBarPosition(): BarPosition | null {
    try {
        const value = JSON.parse(sessionStorage.getItem(BAR_POSITION_KEY) ?? 'null') as BarPosition | null;
        return value && [value.left, value.top, value.width].every(Number.isFinite) ? value : null;
    } catch {
        return null;
    }
}

/** A moved bar stays within the window, its title row always reachable. */
function barOnScreen(position: BarPosition): BarPosition {
    const inset = 8;
    const width = Math.min(position.width, window.innerWidth - 2 * inset);
    return { width, left: Math.min(Math.max(position.left, inset), window.innerWidth - width - inset),
        top: Math.min(Math.max(position.top, inset), Math.max(inset, window.innerHeight - 120)) };
}

export function ProposalReviewBar({ sx }: { sx?: object }) {
    const session = useProposalReviewSession();
    const openPage = React.useContext(ReviewPageLinkContext);
    const proposalId = session?.preview.generation.id ?? 0;
    const [confirming, setConfirming] = useBarState(`${proposalId}:confirming`, false);
    // A save that deletes saved items, or replaces saved text, goes only once the teacher ticks what it does.
    const [replacementConfirmed, setReplacementConfirmed] = useBarState(`${proposalId}:replacementConfirmed`, false);
    const [confirmingEditsOnly, setConfirmingEditsOnly] = useBarState(`${proposalId}:confirmingEditsOnly`, false);
    const [keepReason, setKeepReason] = useBarState(`${proposalId}:keepReason`, '');
    const feedbackDraft = useBarState(`${proposalId}:feedback`, EMPTY_FEEDBACK_DRAFT);
    const [listOpen, setListOpen] = useBarState(`${proposalId}:listOpen`, false);
    // On a phone the open list makes the sticky bar too tall to show a change below it: once the review moves away from
    // the change it was opened at (from the list, Previous or Next, a choice that moves on, or another page), it closes.
    // A wider window keeps it open. The place is kept with the bar's state, so a redrawn bar (a version switch) keeps it.
    const [listPlace, setListPlace] = useBarState<string | null>(`${proposalId}:listPlace`, null);
    const phone = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    // A rewrite is one place, whichever half the version shows (a choice or a version switch can swap them).
    const currentPlace = session?.current ? session.current.pair_key ?? session.current.key : null;
    const closeList = React.useRef(() => {});
    closeList.current = () => { if (listOpen) setListOpen(false); };
    React.useEffect(() => { if (phone && listPlace !== currentPlace) closeList.current(); }, [currentPlace, listPlace, phone]);
    // Like the lesson review, the bar can be moved by its title row (or the arrow keys): it then floats where it was put,
    // for this tab, until put back in place. A phone keeps it in place.
    const barRef = React.useRef<HTMLDivElement | null>(null);
    // Restored within this window (a smaller one than it was moved in, say), so its title row and buttons stay reachable.
    const [moved, setMoved] = React.useState<BarPosition | null>(() => { const saved = readBarPosition(); return saved && barOnScreen(saved); });
    const dragRef = React.useRef<{ pointerId: number; x: number; y: number; start: BarPosition } | null>(null);
    const moveBar = React.useCallback((next: BarPosition | null) => {
        setMoved(next);
        try {
            if (next) sessionStorage.setItem(BAR_POSITION_KEY, JSON.stringify(next));
            else sessionStorage.removeItem(BAR_POSITION_KEY);
        } catch { /* kept for this page only */ }
    }, []);
    const floating = moved !== null && !phone;
    React.useEffect(() => {
        if (!floating) return undefined;
        // Kept on screen when it floats again, and when the window changes; the corrected place is the one remembered.
        const keep = () => setMoved(current => {
            const next = current && barOnScreen(current);
            try { if (next) sessionStorage.setItem(BAR_POSITION_KEY, JSON.stringify(next)); } catch { /* kept for this page only */ }
            return next;
        });
        keep();
        window.addEventListener('resize', keep);
        return () => window.removeEventListener('resize', keep);
    }, [floating]);
    if (!session) return null;
    const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
        if (phone || event.button !== 0 || !event.isPrimary || !barRef.current) return;
        // The row's own controls keep their clicks.
        if (event.target instanceof Element && event.target.closest('button, a, input, [role="button"]')) return;
        const rect = barRef.current.getBoundingClientRect();
        dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, start: { left: rect.left, top: rect.top, width: rect.width } };
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
    };
    const drag = (event: React.PointerEvent<HTMLDivElement>) => {
        const start = dragRef.current;
        if (!start || start.pointerId !== event.pointerId) return;
        moveBar(barOnScreen({ ...start.start, left: start.start.left + event.clientX - start.x, top: start.start.top + event.clientY - start.y }));
    };
    const stopDrag = (event: React.PointerEvent<HTMLDivElement>) => {
        if (dragRef.current?.pointerId !== event.pointerId) return;
        dragRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    };
    const moveWithKeys = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (phone || event.target !== event.currentTarget || !barRef.current) return;
        const step = event.shiftKey ? 48 : 12;
        const delta = ({ ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] } as Record<string, number[]>)[event.key];
        if (!delta) return;
        const rect = barRef.current.getBoundingClientRect();
        const base = moved ?? { left: rect.left, top: rect.top, width: rect.width };
        moveBar(barOnScreen({ ...base, left: base.left + delta[0], top: base.top + delta[1] }));
        event.preventDefault();
    };
    const { stops, otherStops, current, linked, version, saving, attachment, preview, position, total, selectedStops, reviewedCount, unreviewedSelected,
        edits, currentEdited, pendingChoice } = session;
    const editCount = edits.length;
    const editWord = (count: number) => `${count} ${count === 1 ? 'edit' : 'edits'}`;
    const findingCount = preview.warnings?.length ?? 0;
    const sendCount = session.savePlan.send.length;
    // What the chosen changes delete or replace on the saved lesson, which the Save dialog spells out (and, for a
    // replacement proposal, asks the teacher to confirm).
    // What the save does to each kind of saved item: saved cards or exercises each replaced by its new version of the same
    // title, a saved set replaced by the new items kept (a regenerated set rarely pairs one to one), or saved items
    // deleted with nothing new in their place. Undo can bring the saved ones back either way.
    const replacedBy = (stop: IVerbaScholaAiReviewChange) => { const pair = session.rewritePair(stop); return Boolean(pair && session.included(pair)); };
    const kindsDone = ([['plan_section', 'plan card', 'plan cards'], ['exercise', 'exercise', 'exercises']] as const).flatMap(([kind, one, many]) => {
        const removals = selectedStops.filter(stop => stop.kind === `${kind}_removal`);
        if (!removals.length) return [];
        const additions = selectedStops.filter(stop => stop.kind === `${kind}_addition`);
        const items = (count: number, prefix: string) => `${count} ${prefix} ${count === 1 ? one : many}`;
        const saved = items(removals.length, 'saved');
        if (removals.every(replacedBy) && additions.length === removals.length) {
            const versions = removals.length === 1 ? 'its new version' : 'their new versions';
            return [{ text: `replaces ${saved} with ${versions}`, label: `replace ${saved} with ${versions}` }];
        }
        return additions.length ? [{ text: `replaces ${saved} with ${items(additions.length, 'new')}`, label: `replace ${saved} with ${items(additions.length, 'new')}` }]
            : [{ text: `deletes ${saved}`, label: `delete ${saved}` }];
    });
    // A review stored from before each removal was its own change deletes the saved set as a whole.
    const wholeSet = !kindsDone.length && selectedStops.some(stop => stop.group === 'replace') ? [{ text: 'deletes the saved set', label: 'delete the saved set' }] : [];
    const done = [...kindsDone, ...wholeSet];
    const deletes = done.length > 0;
    const deleted = done.map(item => item.text).join(' and ');
    const confirmLabel = done.map(item => item.label).join(' and ').replace(/^./, letter => letter.toUpperCase());
    const rewritten = attachment.confirmReplacement === 'regenerate' ? selectedStops.filter(stop => stop.kind === 'content_replacement').length : 0;
    const needsConfirm = Boolean(attachment.confirmReplacement) && (deletes || rewritten > 0);
    const editsOnly = session.editsOnlyPlan;
    const blocked = session.replay ? null : attachment.applyBlockReason ?? (!preview.can_apply ? 'This proposal can no longer be saved to the lesson.' : null);
    const related = linked.length;
    const otherPages = REVIEW_PAGE_ORDER.filter(value => otherStops.some(stop => session.pageOf(stop) === value));
    const isIncluded = current ? session.included(current) : false;
    const pair = current ? session.rewritePair(current) : null;
    const pairIncluded = pair ? session.included(pair) : false;
    const absence = reviewAbsenceNote(current, version, isIncluded, session.replay, pair ? { pairIncluded } : undefined);
    const labels = session.replay ? replayLabels : versionLabels;
    // Moving on from this page's last stop opens the next page, so the navigator covers the whole proposal.
    const crossPage = Boolean(openPage) && otherStops.length > 0 && !session.replay;
    const shown = crossPage ? total : stops.length;
    const chosen = session.chosenStops.length;
    const note = { fontFamily: INTER, fontSize: 12.5, color: SLATE };
    const comparison = current ? stopComparison(current, preview) : [];
    // Name the linked changes, so the teacher sees what else a click includes or leaves out.
    const linkedNames = linked.slice(0, 3).map(stop => session.label(stop)).join('; ') + (linked.length > 3 ? `; and ${linked.length - 3} more` : '');
    // Only what the teacher needs while looking at a change stays on screen; the rest scrolls with the page below it.
    return <>
    <Box ref={barRef} role="region" aria-label="Proposal review" data-testid="proposal-review-bar" sx={{
        ...(floating && moved ? {
            // Moved by the teacher: it floats where it was put, over the page, below the copilot drawer and dialogs.
            position: 'fixed', left: moved.left, top: moved.top, width: moved.width, zIndex: 1150,
            maxHeight: `calc(100dvh - ${moved.top}px - 8px)`,
        } : {
            position: 'sticky', top: { xs: 8, lg: 80 }, zIndex: 5,
            // Part of the window is always kept for the lesson (a quarter on a phone, more on a short laptop window), so
            // the change under review has room below the bar. What does not fit scrolls inside it, between its pinned top
            // (the versions, the change and Include) and its pinned save row: the notes, the change list and the
            // replacement set.
            maxHeight: { xs: 'calc(100dvh - 8px - max(140px, 25dvh))', md: 'calc(100dvh - 8px - max(160px, 35dvh))', lg: 'calc(100dvh - 80px - max(160px, 35dvh))' },
        }),
        mb: 1, borderRadius: '14px',
        overflowY: 'auto', overscrollBehavior: 'contain',
        border: `1px solid ${BLUE}`, backgroundColor: '#fff', boxShadow: '0 6px 18px rgba(22, 58, 99, 0.12)', ...sx,
    }}>
        <Stack spacing={0.75} sx={{ p: { xs: 1, sm: 1.25 } }}>
            {/* Pinned at the top of the bar while its middle scrolls. */}
            <Stack spacing={0.75} data-testid="proposal-review-controls" sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff',
                mx: { xs: -1, sm: -1.25 }, mt: { xs: -1, sm: -1.25 }, px: { xs: 1, sm: 1.25 }, pt: { xs: 1, sm: 1.25 }, pb: 0.5 }}>
            {/* The title gives way to the version switch on a phone; wider, they share the row, by which the bar is moved. */}
            <Stack direction="row" alignItems="center" spacing={1} {...(phone ? {} : {
                role: 'group', 'aria-label': 'Proposal review title bar', tabIndex: 0,
                title: 'Drag this bar to move the review; use arrow keys to move by 12 pixels, or Shift and an arrow key to move by 48 pixels',
                onPointerDown: startDrag, onPointerMove: drag, onPointerUp: stopDrag, onPointerCancel: stopDrag, onKeyDown: moveWithKeys,
                sx: { cursor: 'move', touchAction: 'none', borderRadius: '8px' },
            })}>
                <Typography component="h2" sx={{ display: { xs: 'none', md: 'block' }, flex: 1, fontFamily: INTER, fontWeight: 700, color: NAVY, fontSize: 14, minWidth: 0 }}>
                    {session.replay ? 'Saved version of an AI proposal' : 'Reviewing AI proposal'}
                </Typography>
                <ToggleButtonGroup exclusive size="small" value={version} aria-label="Lesson version" sx={{ flex: { xs: 1, md: 'none' }, '& .MuiToggleButton-root': { flex: { xs: 1, md: 'none' } } }}
                    onChange={(_event, value: ReviewVersion | null) => { if (value) session.setVersion(value); }}>
                    {(['before', 'proposal', 'yours'] as const).map(value => <ToggleButton key={value} value={value}
                        sx={{ px: { xs: 0.75, sm: 1.5 }, minHeight: 34, textTransform: 'none', fontFamily: INTER, fontWeight: 700, whiteSpace: 'nowrap' }}>{labels[value]}</ToggleButton>)}
                </ToggleButtonGroup>
                {floating && <IconButton aria-label="Put the review back in place" title="Put the review back in place" onClick={() => moveBar(null)} sx={{ width: 36, height: 36 }}>
                    <VerticalAlignTopRoundedIcon /></IconButton>}
                <IconButton aria-label="Close proposal review" onClick={session.close} disabled={saving} sx={{ width: 36, height: 36 }}><CloseRoundedIcon /></IconButton>
            </Stack>
            {stops.length > 0 ? <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton aria-label="Previous change" onClick={() => session.go(-1)} disabled={shown < 2 || saving}
                    sx={{ width: 36, height: 36, border: `1px solid ${BORDER}` }}><ArrowBackIosNewRoundedIcon fontSize="small" /></IconButton>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }} aria-live="polite">
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: NAVY, fontSize: 13 }}>
                        {crossPage ? `Change ${position} of ${total}` : `Change ${session.index + 1} of ${stops.length}${otherStops.length ? ' on this page' : ''}`}
                    </Typography>
                    {current && <Typography data-testid="proposal-review-current" sx={{ fontFamily: INTER, color: SLATE, fontSize: 12.5, overflowWrap: 'anywhere' }}>{session.label(current)}</Typography>}
                    {current && comparison.length > 0 && <ReviewComparison key={current.key} rows={comparison} stateKey={`${preview.generation.id}:${current.key}`} />}
                </Box>
                <IconButton aria-label="Next change" onClick={() => session.go(1)} disabled={shown < 2 || saving}
                    sx={{ width: 36, height: 36, border: `1px solid ${BORDER}` }}><ArrowForwardIosRoundedIcon fontSize="small" /></IconButton>
            </Stack> : <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13 }}>No changes on this page.</Typography>}
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                {current && !session.replay && !pair && <FormControlLabel sx={{ mx: 0, mr: 'auto', '& .MuiFormControlLabel-label': { fontFamily: INTER, fontSize: 13.5, color: NAVY } }}
                    control={<Checkbox size="small" checked={isIncluded} disabled={saving || session.held} onChange={event => session.setIncluded(current, event.target.checked)} />}
                    label="Include in your version" />}
                {current && <Typography data-testid="proposal-review-status" sx={{ fontFamily: INTER, fontSize: 12, fontWeight: 700, color: isIncluded || pairIncluded ? SUCCESS_TEXT : SLATE, ...(session.replay || pair ? { mr: 'auto' } : {}) }}>
                    {pair ? rewriteStatus(isIncluded, pairIncluded, session.replay)
                        : session.replay ? (isIncluded ? 'Kept in this save' : 'Left out of this save')
                        : isIncluded ? 'Included in your version' : 'Not in your version'}{currentEdited ? ' · edited by you' : ''}
                </Typography>}
                {session.nextUnreviewed && <Button size="small" disabled={saving} onClick={() => session.goToStop(session.nextUnreviewed!)} sx={{ minHeight: 30 }}>Next not reviewed</Button>}
                {/* The findings stay in the copilot, one copy; on a narrow window it is closed during review, so the bar links there. */}
                {findingCount > 0 && session.showFindings && !session.replay && <Button size="small" onClick={session.showFindings}
                    sx={{ minHeight: 30, color: WARNING_TEXT }}>Proposal findings ({findingCount})</Button>}
                {session.chat && !session.replay && preview.generation.proposal_status === 'pending' && preview.review_context?.message_id && <Button size="small" disabled={saving || session.chat.disabled}
                    onClick={() => { session.chat?.refine(preview); session.showComposer?.(); }} sx={{ minHeight: 30 }}>Ask copilot to refine</Button>}
                <Button size="small" aria-expanded={listOpen} onClick={() => { setListPlace(currentPlace); setListOpen(value => !value); }} sx={{ minHeight: 30 }}>{listOpen ? 'Hide the list of changes' : `List all ${total} changes`}</Button>
            </Stack>
            </Stack>
            {current && pair && (() => {
                const [removal, addition] = removalKinds.has(current.kind) ? [current, pair] : [pair, current];
                const noun = removal.kind === 'exercise_removal' ? 'exercise' : 'card';
                const [gone, added] = [session.included(removal), session.included(addition)];
                const outcome = gone && added ? `The new ${noun} replaces the saved one.` : added ? `Both are kept: the saved ${noun} and the new one.`
                    : gone ? `The saved ${noun} is deleted and the new one left out.` : `The saved ${noun} stays as it is.`;
                // A rewrite's choice answers it: the review moves on to the next change.
                const choose = (removed: boolean, new_: boolean) => session.setManyIncluded([{ stop: removal, include: removed }, { stop: addition, include: new_ }], true);
                return <Box data-testid="proposal-review-rewrite" sx={{ ...note, color: NAVY }}>
                    This rewrites a saved {noun} of the same title. {outcome}{' '}
                    {!session.replay && <>
                        {/* The current choice is marked, and stays clickable: choosing it again confirms it and moves on. */}
                        {([['Use the new one', true, true], ['Keep both', false, true], ['Keep the saved one', false, false]] as const).map(([text, removed, new_]) =>
                            <Button key={text} size="small" variant={gone === removed && added === new_ ? 'outlined' : 'text'} aria-pressed={gone === removed && added === new_}
                                disabled={saving || session.held} onClick={() => choose(removed, new_)}>{text}</Button>)}
                    </>}
                </Box>;
            })()}
            {current && (() => {
                // Changes of one apply group are saved together; say why, so a shared checkbox is no surprise.
                const together = session.orderedStops.filter(stop => stop.group === current.group && stop.key !== current.key).length;
                if (!together) return null;
                const reason = current.group.startsWith('step:') ? 'they change the same activity, which is saved whole so its answers, lines and settings stay valid together (one correct answer, lines in order)'
                    : current.group.startsWith('exercise:') ? 'they change the same exercise’s settings'
                        : current.group.startsWith('vocabulary_target:') || current.group.startsWith('grammar_target:') ? 'a word’s characters, pinyin and meaning must match, so they are saved together'
                            : 'they are written by one change';
                return <Typography data-testid="proposal-review-group-reason" sx={note}>
                    Saved together with {together} other {together === 1 ? 'change' : 'changes'}: {reason}.
                </Typography>;
            })()}
            {current && related > 0 && <Typography data-testid="proposal-review-linked" sx={note}>
                {pair ? (pairIncluded ? `Keeping the saved one also leaves out: ${linkedNames}.` : `Using the new one also includes: ${linkedNames}.`)
                    : isIncluded ? `Leaving it out also leaves out: ${linkedNames}.` : `Including it also includes: ${linkedNames}.`}
            </Typography>}
            {listOpen && <ReviewChangeList session={session} />}
            {absence && <Typography role="status" sx={{ ...note, color: NAVY, backgroundColor: SOFT_BLUE, borderRadius: '8px', px: 1, py: 0.5 }}>{absence}</Typography>}
            {session.outdated ? <Box role="status" data-testid="proposal-review-outdated" sx={{ ...note, color: WARNING_TEXT }}>
                {/* The draft is stored per teacher and proposal: only a save of this review (or finding the proposal closed), or REVIEW_STORE_DAYS without a write, removes it. */}
                {session.closedElsewhere === 'applied' ? 'This proposal was already saved in another tab or window, from a different choice of changes, so the changes chosen here were not saved and can no longer be; View saved result in the copilot shows what was.'
                    : session.savedElsewhere ? 'This proposal was dismissed in another tab or window, so the changes chosen here were not saved and can no longer be.'
                    : `The lesson changed after this proposal was made, so its AI changes can no longer be saved. ${reviewStoreWorks
                        ? `Your choices and edits stay stored in this browser after you close the review (Review proposal opens them again) until this review is saved, or until ${REVIEW_STORE_DAYS} days after you last worked on it.`
                        : 'This browser could not store your choices and edits, so they are kept only while this page stays open.'}`}
                {/* A closed proposal takes no save at all, so its edits cannot go with one. */}
                {session.savedElsewhere ? (editCount ? ` It was closed there, so your ${editWord(editCount)} cannot be saved with it either: redo ${editCount === 1 ? 'it' : 'them'} on the lesson after closing this review if still wanted, or ask the copilot for a new proposal.`
                    : ' Ask the copilot for a new proposal.')
                : editsOnly.send.length ? <> {editsOnly.unsaveable.length
                    ? `${editsOnly.send.length} of your ${editsOnly.send.length + editsOnly.unsaveable.length} edits can still be saved to the current lesson; ${editsOnly.unsaveable.length} ${editsOnly.unsaveable.length === 1 ? 'is' : 'are'} to items only this proposal adds, so ${editsOnly.unsaveable.length === 1 ? 'it' : 'they'} cannot.`
                    : `Your ${editWord(editsOnly.send.length)} can still be saved to the current lesson.`} If one is to text that changed on the lesson meanwhile, nothing is saved and that edit is listed here.
                    {' '}<Button size="small" disabled={saving || Boolean(session.earlierUnsaved)}
                        onClick={() => editsOnly.unsaveable.length ? setConfirmingEditsOnly(true) : session.saveEditsOnly()}>Save my edits only</Button></>
                    : editCount ? ' Your edits are all to items only this proposal adds, so they cannot be saved without it. Ask the copilot for a new proposal.' : ' Ask the copilot for a new proposal.'}
            </Box> : blocked && <Typography role="status" sx={{ ...note, color: WARNING_TEXT }}>{blocked}</Typography>}
            {session.earlierUnsaved && !session.replay && <Typography role="status" data-testid="proposal-review-earlier-unsaved" sx={{ ...note, color: WARNING_TEXT }}>
                {session.earlierUnsaved}
            </Typography>}
            {session.rejectedEdits.length > 0 && <Box role="status" data-testid="proposal-review-rejected" sx={{ ...note, color: WARNING_TEXT }}>
                {session.rejectedEdits.length === 1 ? 'This edit' : 'These edits'} stopped the save (nothing was saved):
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{session.rejectedEdits.map((item, index) => <li key={`${reviewEditKey(item.edit.kind, item.edit)}:${index}`}>
                    {editSummary(item.edit)}{item.reason === 'changed' && item.edit.action === 'order'
                        ? ' (the list changed on the lesson since you reviewed it)'
                        : item.reason === 'changed' && item.current
                        ? ` (now: ${Object.entries(item.current).map(([field, value]) => `${field}: ${typeof value === 'string' ? value : JSON.stringify(value)}`).join('; ')})`
                        : item.reason === 'not_found' ? ' (no longer in the lesson)' : item.message ? ` (${refusalText(item.message)})` : ''}
                </li>)}</Box>
                {session.rejectedEdits.some(item => item.reason === 'changed' && item.current) && <>
                    {' '}<Button size="small" disabled={saving} onClick={session.keepRejected}>Keep mine over the current version</Button> makes the next save replace
                    {' '}what the lesson holds now (edit yours again first if you like; a change made after this still stops the save), or</>}
                {' '}<Button size="small" disabled={saving} onClick={session.dropRejected}>Leave {session.rejectedEdits.length === 1 ? 'it' : 'them'} out</Button> and save the rest.
            </Box>}
            {session.saveError && <Typography role="alert" sx={{ ...note, color: ERROR_TEXT }}>Not saved: {session.saveError}</Typography>}
            {/* Pinned at the bottom of the bar while its middle scrolls. */}
            <Box sx={{ position: 'sticky', bottom: 0, zIndex: 2, backgroundColor: '#fff', mx: { xs: -1, sm: -1.25 }, px: { xs: 1, sm: 1.25 }, py: 0.5 }}>
            {session.replay ? <Typography data-testid="proposal-review-selection" sx={{ ...note, fontWeight: 700, color: NAVY }}>
                {chosen} of {total} {total === 1 ? 'change' : 'changes'} saved{editCount ? ` · ${editWord(editCount)} of yours` : ''} · Shown as saved, read-only
            </Typography> : <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography data-testid="proposal-review-selection" sx={{ ...note, fontWeight: 700, color: NAVY, flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }}>
                    {chosen} of {total} {total === 1 ? 'change' : 'changes'} selected · {reviewedCount} reviewed{editCount ? ` · ${editWord(editCount)}` : ''} · Not saved
                </Typography>
                <Button size="small" disabled={saving || session.held || selectedStops.length === preview.review_changes!.length} onClick={session.includeAll}>Include all</Button>
                <Button size="small" disabled={saving || session.held || chosen === 0} onClick={session.clearSelection}>Clear selection</Button>
                {/* With nothing chosen and no edit, the same button finishes the review instead (after asking): the proposal is
                    dismissed and the lesson stays. Not once another tab closed the proposal: there is nothing left to dismiss.
                    Either way an open edit not kept yet (an emptied title, say) is asked about before the confirmation. */}
                {chosen === 0 && editCount === 0
                    ? <Button variant="outlined" sx={{ minHeight: 36, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
                        disabled={saving || session.held || Boolean(session.earlierUnsaved) || Boolean(session.closedElsewhere)}
                        onClick={() => session.guard(() => { setReplacementConfirmed(false); setConfirming(true); })}>Keep the saved lesson</Button>
                    : <Button variant="contained" sx={{ ...aiButtonSx, minHeight: 36, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} disabled={saving || Boolean(blocked) || session.outdated || Boolean(session.earlierUnsaved)}
                        onClick={() => session.guard(() => { setReplacementConfirmed(false); setConfirming(true); })}>
                        {`Save your version · ${[...(chosen ? [`${chosen} ${chosen === 1 ? 'change' : 'changes'}`] : []), ...(editCount ? [editWord(editCount)] : [])].join(', ')}`}
                    </Button>}
                {saving && <CircularProgress size={18} aria-label="Saving" />}
            </Stack>}
            </Box>
            {!session.replay && <ReplacementSet session={session} />}
            <Dialog open={session.pendingLeave} onClose={() => session.resolveLeave(false)} aria-labelledby="proposal-review-leave-title">
                <DialogTitle id="proposal-review-leave-title">Discard your open edit?</DialogTitle>
                <DialogContent>
                    <DialogContentText>An editor on the page has changes you have not kept in Your version yet. Keep them first, or discard them and continue.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => session.resolveLeave(false)}>Back to editing</Button>
                    <Button variant="contained" onClick={() => session.resolveLeave(true)}>Discard and continue</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(pendingChoice)} onClose={() => session.resolveChoice('cancel')} aria-labelledby="proposal-review-choice-title">
                <DialogTitle id="proposal-review-choice-title">{pendingChoice?.dropped.length ? 'Drop your edits?' : 'Replace your edits?'}</DialogTitle>
                <DialogContent>
                    {pendingChoice?.replaced.length ? <DialogContentText>
                        The AI also changes {pendingChoice.replaced.flatMap(item => item.fields).length === 1 ? 'a field' : 'fields'} you edited
                        ({[...new Set(pendingChoice.replaced.flatMap(item => item.fields))].join(', ')}). Keep your edits, or use the AI suggestion there.
                    </DialogContentText> : null}
                    {pendingChoice?.dropped.length ? <DialogContentText sx={{ mt: 1 }}>
                        This leaves out or removes {pendingChoice.dropped.length === 1 ? 'an item' : `${pendingChoice.dropped.length} items`} you edited; your edits to {pendingChoice.dropped.length === 1 ? 'it' : 'them'} would be dropped.
                    </DialogContentText> : null}
                    {pendingChoice?.dropped.length ? <Box component="ul" data-testid="proposal-review-dropped" sx={{ m: 0, mt: 0.5, pl: 2.5, fontFamily: INTER, fontSize: 13.5, color: SLATE }}>
                        {pendingChoice.dropped.map((edit, index) => <li key={`${reviewEditKey(edit.kind, edit)}:${index}`}>{editSummary(edit)}</li>)}
                    </Box> : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => session.resolveChoice('cancel')}>Back to review</Button>
                    {pendingChoice?.replaced.length ? <>
                        <Button onClick={() => session.resolveChoice('keep')}>{pendingChoice.dropped.length ? 'Drop them, keep my other edits' : 'Keep my edits'}</Button>
                        <Button variant="contained" sx={aiButtonSx} onClick={() => session.resolveChoice('ai')}>Use the AI suggestion</Button>
                    </> : <Button variant="contained" onClick={() => session.resolveChoice('keep')}>Drop my edits</Button>}
                </DialogActions>
            </Dialog>
            <Dialog open={confirmingEditsOnly} onClose={() => setConfirmingEditsOnly(false)} aria-labelledby="proposal-review-edits-only-title">
                <DialogTitle id="proposal-review-edits-only-title">Save your edits only?</DialogTitle>
                <DialogContent>
                    <DialogContentText>{`Your ${editWord(editsOnly.send.length)} ${editsOnly.send.length === 1 ? 'is' : 'are'} saved to the current lesson, as this proposal’s saved version, and the proposal closes; none of its AI changes are kept.`}</DialogContentText>
                    <UnsaveableEdits items={editsOnly.unsaveable}
                        lead={`${editsOnly.unsaveable.length === 1 ? 'This edit is' : `These ${editsOnly.unsaveable.length} edits are`} not saved: ${editsOnly.unsaveable.length === 1 ? 'it is' : 'they are'} to items only this proposal adds. Redo ${editsOnly.unsaveable.length === 1 ? 'it' : 'them'} by hand if still wanted:`} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmingEditsOnly(false)}>Back to review</Button>
                    <Button variant="contained" onClick={() => { setConfirmingEditsOnly(false); session.saveEditsOnly(); }}>Save my edits only</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirming} onClose={() => setConfirming(false)} aria-labelledby="proposal-review-save-title">
                <DialogTitle id="proposal-review-save-title">{chosen === 0 && sendCount === 0 ? 'Keep the saved lesson?' : 'Save your version?'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{chosen ? `${chosen} of ${total} AI ${total === 1 ? 'change' : 'changes'} will be saved to the lesson.`
                        : sendCount ? 'No AI changes are kept: your edits alone are saved, as this proposal’s saved version, and the proposal closes.'
                            : 'None of the AI changes are kept: the proposal is dismissed and the lesson stays as it is.'}</DialogContentText>
                    {chosen > 0 && total > chosen && <DialogContentText sx={{ mt: 1 }}>
                        {total - chosen} {total - chosen === 1 ? 'change is' : 'changes are'} left out. Saving closes this proposal, so the left-out changes are not offered again; ask the copilot again if you want them later.
                    </DialogContentText>}
                    {sendCount > 0 && <DialogContentText sx={{ mt: 1 }}>
                        Your {editWord(sendCount)} {chosen ? `${sendCount === 1 ? 'is' : 'are'} saved together with the AI changes, as one save` : 'will also be saved'}{chosen ? ': Undo of this save reverses both' : ': Undo of this save reverses them'}. If an edit is to text that changed on the lesson meanwhile, nothing is saved and the bar lists that edit.
                    </DialogContentText>}
                    {session.savePlan.unsaveable.length > 0 && <UnsaveableEdits items={session.savePlan.unsaveable}
                        lead={`${session.savePlan.unsaveable.length === 1 ? 'This edit is' : `These ${session.savePlan.unsaveable.length} edits are`} not saved: ${session.savePlan.unsaveable.length === 1 ? 'it is' : 'they are'} to items this choice of changes leaves out or removes.`} />}
                    {unreviewedSelected.length > 0 && <DialogContentText sx={{ mt: 1, color: WARNING_TEXT }}>
                        {unreviewedSelected.length} of the included {unreviewedSelected.length === 1 ? 'change has' : 'changes have'} not been shown to you yet.
                    </DialogContentText>}
                    {findingCount > 0 && <DialogContentText data-testid="proposal-review-save-findings" sx={{ mt: 1, color: WARNING_TEXT }}>
                        This proposal came with {findingCount} {findingCount === 1 ? 'finding' : 'findings'} to check (flagged Chinese, answers, coverage or pictures).
                        {session.showFindings && <>{' '}<Button size="small" onClick={() => { setConfirming(false); session.showFindings!(); }}>Show findings</Button></>}
                    </DialogContentText>}
                    <DialogContentText sx={{ mt: 1 }}>Saving does not publish the lesson or mark it reviewed.</DialogContentText>
                    {chosen === 0 && sendCount === 0 && <Box sx={{ mt: 2, display: 'grid' }}><FeedbackReasonField label="Dismiss reason (optional)" value={keepReason} onChange={setKeepReason} /></Box>}
                    {deletes && <DialogContentText sx={{ mt: 1, color: WARNING_TEXT }}>
                        Saving {deleted}. Undo of this save can restore the saved ones while nothing it saved has changed; to keep saved ones, choose Keep the saved set (or Keep the saved one for a rewrite), or leave their removals out.
                    </DialogContentText>}
                    {rewritten > 0 && <DialogContentText sx={{ mt: 1, color: WARNING_TEXT }}>
                        Saving replaces the saved text of {rewritten} {rewritten === 1 ? 'item' : 'items'}, including any manual edits to {rewritten === 1 ? 'it' : 'them'}. Undo of this save can restore it while nothing it saved has changed.
                    </DialogContentText>}
                    {needsConfirm && <FormControlLabel sx={{ mt: 1 }} control={<Checkbox checked={replacementConfirmed} onChange={event => setReplacementConfirmed(event.target.checked)} />}
                        label={deletes ? confirmLabel : `Replace the saved text of ${rewritten} ${rewritten === 1 ? 'item' : 'items'}`} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(false)}>Back to review</Button>
                    <Button variant="contained" sx={aiButtonSx} disabled={needsConfirm && !replacementConfirmed} onClick={() => { setConfirming(false); session.save(chosen === 0 && sendCount === 0 ? keepReason || undefined : undefined); }}>
                        {chosen === 0 && sendCount === 0 ? 'Keep the saved lesson' : 'Save your version'}</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    </Box>
    {/* Details for this review, read once: they scroll with the page instead of covering it. */}
    <Stack data-testid="proposal-review-details" spacing={0.5} sx={{ mb: 2, px: 0.5 }}>
        {session.carriedOver && <Typography role="status" data-testid="proposal-review-carried" sx={{ ...note, color: NAVY, backgroundColor: SOFT_BLUE, borderRadius: '8px', px: 1, py: 0.5 }}>
            This refines an earlier proposal: your choices and edits from it were carried over. Its suggestions may have changed, so review them again.
        </Typography>}
        {session.setAside.length > 0 && <Box role="status" data-testid="proposal-review-set-aside" sx={{ ...note, color: WARNING_TEXT }}>
            {session.setAside.length} of your edits from the earlier proposal {session.setAside.length === 1 ? 'was' : 'were'} not carried over:
            {' '}this proposal no longer creates {session.setAside.length === 1 ? 'that item' : 'those items'}, so {session.setAside.length === 1 ? 'it is' : 'they are'} not
            {' '}part of Your version and will not be saved. Redo {session.setAside.length === 1 ? 'it' : 'them'} on the page if still wanted:
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{session.setAside.map((edit, index) => <li key={`${reviewEditKey(edit.kind, edit)}:${index}`}>{editSummary(edit)}</li>)}</Box>
            <Button size="small" onClick={session.dismissSetAside}>Dismiss</Button>
        </Box>}
        {otherPages.length > 0 && <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={note}>
                {otherStops.length} {otherStops.length === 1 ? 'change is' : 'changes are'} on {otherPages.map(value => pageNames[value]).join(' and ')}.
            </Typography>
            {session.replay ? <Typography sx={note}>Show the saved result there to see them.</Typography>
                : openPage && otherPages.map(value => <Button key={value} size="small" disabled={saving}
                onClick={() => session.guard(() => openPage(value, preview.generation.id, { change: 'first', version }))}>Review on {pageNames[value]}</Button>)}
        </Stack>}
        {chosen === 0 && editCount === 0 && !session.replay && <Typography data-testid="proposal-review-start" sx={note}>
            Nothing is selected, so nothing will be saved yet. Include changes one at a time, or Include all and leave out the ones you don't want; Keep the saved lesson dismisses the proposal.
        </Typography>}
        {!session.replay && <MessageFeedback key={proposalId} draftState={feedbackDraft} feedback={preview.review_context?.feedback} disabled={saving} label="Give feedback on this proposal" onSubmit={session.rate} />}
        <Typography data-testid="proposal-review-edit-hint" sx={note}>
            {session.replay ? 'Before is the lesson this proposal was made from; Saved version is what its save wrote, with your edits. Nothing here can be changed.'
                : version === 'yours' ? 'You can edit Your version on the page; your edits are saved with it.' : 'To edit, switch to Your version.'}
        </Typography>
    </Stack>
    </>;
}

export function ReviewUnavailable({ message }: { message: string }) {
    return <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>;
}

/** Scroll to and highlight the current stop's element, retrying while the page renders it. */
export function useReviewFocus(session: ProposalReviewSession | null, selectors: string[],
    options: { focus?: boolean; /** Changes when the page remounts its content, replacing the marked element. */ refreshKey?: string | number } = {}) {
    const key = session ? `${session.preview.generation.id}:${session.focusKey}:${selectors.join('|')}:${options.refreshKey ?? ''}` : '';
    React.useEffect(() => {
        if (!session || !selectors.length) return;
        let marked: HTMLElement | null = null;
        let flashTimer = 0;
        const correctionTimers: number[] = [];
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // The first selector with a shown element: a matching element can sit in a collapsed panel or a closed drawer.
        const visible = (element: HTMLElement) => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
        const mark = () => {
            const element = selectors.map(selector => {
                try { return [...document.querySelectorAll<HTMLElement>(selector)].find(visible) ?? null; } catch { return null; }
            }).find(Boolean) ?? null;
            if (!element) return false;
            marked = element;
            element.setAttribute('data-review-current', 'true');
            element.removeAttribute('data-review-flash');
            void element.offsetWidth;
            element.setAttribute('data-review-flash', 'true');
            flashTimer = window.setTimeout(() => element.removeAttribute('data-review-flash'), 2000);
            // The change lands just below the review bar, which stays on screen above it.
            // Measured each time: the bar can change height after the first placement (a list closing, a note appearing).
            const reviewBar = () => document.querySelector<HTMLElement>('[data-testid="proposal-review-bar"]');
            const belowBar = () => {
                const bar = reviewBar();
                return bar ? (parseFloat(getComputedStyle(bar).top) || 0) + bar.offsetHeight + 12 : 12;
            };
            // A change marked as a whole panel can hold the bar itself (a new exercise): then the page scrolls until the
            // bar sticks, so the panel's content shows right below it.
            const holdsBar = () => { const bar = reviewBar(); return Boolean(bar && element.contains(bar)); };
            const place = (behavior: ScrollBehavior) => {
                const bar = reviewBar();
                if (bar && holdsBar()) {
                    // Where the bar sits in the page's flow (a stuck bar's own box says only where it sticks): below what
                    // comes before it, or at the top of its parent's content.
                    const before = bar.previousElementSibling as HTMLElement | null;
                    const parent = bar.parentElement!;
                    const flowTop = (before ? before.getBoundingClientRect().bottom
                        : parent.getBoundingClientRect().top + (parseFloat(getComputedStyle(parent).paddingTop) || 0)) + (parseFloat(getComputedStyle(bar).marginTop) || 0);
                    const offset = flowTop - (parseFloat(getComputedStyle(bar).top) || 0);
                    let panel: HTMLElement | null = bar.parentElement;
                    while (panel && !(/(auto|scroll)/.test(getComputedStyle(panel).overflowY) && panel.scrollHeight > panel.clientHeight)) panel = panel.parentElement;
                    if (panel && panel !== document.body && panel !== document.documentElement) panel.scrollBy({ top: offset, behavior });
                    else window.scrollBy({ top: offset, behavior });
                    return;
                }
                const below = belowBar();
                // Scrolled by the distance itself: the page or the nearest scrolling panel holding the change.
                let scroller: HTMLElement | null = element.parentElement;
                while (scroller && !(/(auto|scroll)/.test(getComputedStyle(scroller).overflowY) && scroller.scrollHeight > scroller.clientHeight)) scroller = scroller.parentElement;
                const top = element.getBoundingClientRect().top;
                if (scroller && scroller !== document.body && scroller !== document.documentElement) {
                    scroller.scrollBy({ top: top - Math.max(below, scroller.getBoundingClientRect().top + 12), behavior });
                } else window.scrollBy({ top: top - below, behavior });
            };
            place(reduceMotion ? 'auto' : 'smooth');
            // Notes and panels that render just after can shift the page; put the change back if it slid under the bar or off screen.
            for (const delay of [350, 900]) correctionTimers.push(window.setTimeout(() => {
                if (!element.isConnected || element.getAttribute('data-review-current') !== 'true') return;
                const rect = element.getBoundingClientRect();
                if (holdsBar() || rect.top < belowBar() - 14 || rect.top > window.innerHeight - 40) place('auto');
            }, delay));
            if (options.focus) element.focus?.({ preventScroll: true });
            return true;
        };
        let observer: MutationObserver | null = null;
        let stopTimer = 0;
        const frame = requestAnimationFrame(() => {
            if (mark()) return;
            observer = new MutationObserver(() => { if (mark()) observer?.disconnect(); });
            // A collapsed panel can open by a style or class change alone.
            observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
            stopTimer = window.setTimeout(() => observer?.disconnect(), 4000);
        });
        return () => {
            cancelAnimationFrame(frame); observer?.disconnect(); window.clearTimeout(stopTimer); window.clearTimeout(flashTimer);
            correctionTimers.forEach(timer => window.clearTimeout(timer));
            const element = marked as HTMLElement | null;
            element?.removeAttribute('data-review-current'); element?.removeAttribute('data-review-flash');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
}
